import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Slider,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ContentCopy from '@mui/icons-material/ContentCopy';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import EditOutlined from '@mui/icons-material/EditOutlined';
import CloudUploadRounded from '@mui/icons-material/CloudUploadRounded';
import RotateRight from '@mui/icons-material/RotateRight';
import Add from '@mui/icons-material/Add';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import { toast } from 'react-toastify';
import { PUBLIC_APP_ORIGIN } from '../../config/api';
import * as api from '../../services/api';
import { useI18n } from '../../i18n/useI18n';
import UserPageShell, { userCardEnterSx } from '../shared/UserPageShell';

type AlbumForm = {
  name: string;
  slug: string;
  coverImageUrl: string;
  coverIconImageUrl: string;
  backgroundImageUrl: string;
  isActive: boolean;
};

const defaultForm: AlbumForm = {
  name: '',
  slug: '',
  coverImageUrl: '',
  coverIconImageUrl: '',
  backgroundImageUrl: '',
  isActive: true,
};

type PreviewPageIcon = {
  id: string;
  url: string;
  xPct: number;
  yPct: number;
  sizePct: number;
  rotateDeg: number;
};

type PreviewIconHandle = 'tl' | 'br';

const toSlug = (v: string) =>
  String(v || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const generateAutoSlug = () => `album-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
const MIN_PREVIEW_ZOOM = 0.5;
const MAX_PREVIEW_ZOOM = 2;
const PREVIEW_ZOOM_STEP = 0.1;
const FRAME_SIZE_PRIORITY = ['2x4', '4x2', '2x6', '6x2', '4x6', '6x4', '3x4', '4x3'];
const normalizeFrameSize = (value?: string) => String(value || '').trim().toLowerCase();
const FRAME_BG_CROP_WIDTH = 1000;
const FRAME_BG_CROP_HEIGHT = 1450;
const FRAME_BG_ZOOM_MIN = 1;
const FRAME_BG_ZOOM_MAX = 3;
const FRAME_BG_PAN_LIMIT = 100;
const PINK = '#e86a8a';
const PINK_SOFT = '#ff8ba7';

const albumFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    bgcolor: '#fff',
    boxShadow: `0 1px 3px ${alpha(PINK, 0.06)}`,
    '& fieldset': { borderColor: alpha(PINK_SOFT, 0.42) },
    '&:hover fieldset': { borderColor: PINK },
    '&.Mui-focused': {
      boxShadow: `0 0 0 3px ${alpha(PINK, 0.14)}`,
      '& fieldset': { borderWidth: 1.5, borderColor: PINK },
    },
  },
};

const uploadDropSx = {
  border: '1px dashed #d8d8d8',
  borderRadius: 2,
  px: 2,
  py: 2.25,
  width: '100%',
  minHeight: 118,
  textAlign: 'center' as const,
  cursor: 'pointer',
  bgcolor: '#fff',
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.75,
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: '#ec407a',
    backgroundColor: '#fff7fb',
  },
};

const normalizeRotationDeg = (value: number) => {
  if (!Number.isFinite(value)) return 0;
  let next = value % 360;
  if (next > 180) next -= 360;
  if (next < -180) next += 360;
  return next;
};

const PublicAlbumPages: React.FC = () => {
  const { admin: a, pages: t, formatMessage } = useI18n();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [albums, setAlbums] = useState<api.PublicMenuAlbum[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingAlbum, setCreatingAlbum] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<api.PublicMenuAlbum | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<AlbumForm>(defaultForm);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState('');
  const [backgroundPreviewUrl, setBackgroundPreviewUrl] = useState('');
  const [previewZoom, setPreviewZoom] = useState(1);
  const [centerPreviewMode, setCenterPreviewMode] = useState<'page' | 'album'>('page');
  const [selectedPageFrameId, setSelectedPageFrameId] = useState<string | null>(null);
  const [togglingFrameId, setTogglingFrameId] = useState<string | null>(null);
  const [reorderingFrameId, setReorderingFrameId] = useState<string | null>(null);
  const [orderDraftByFrameId, setOrderDraftByFrameId] = useState<Record<string, string>>({});
  const [selectedFrameSizes, setSelectedFrameSizes] = useState<string[]>([]);
  const [availableFrames, setAvailableFrames] = useState<api.CustomFrame[]>([]);
  const [selectedFrameIds, setSelectedFrameIds] = useState<string[]>([]);
  const [mainImageByFrameId, setMainImageByFrameId] = useState<Record<string, string>>({});
  const [frameBackgroundByFrameId, setFrameBackgroundByFrameId] = useState<Record<string, string>>({});
  const [frameIconByFrameId, setFrameIconByFrameId] = useState<Record<string, string>>({});
  const [previewIconsByFrameId, setPreviewIconsByFrameId] = useState<Record<string, PreviewPageIcon[]>>({});
  const [activePreviewIcon, setActivePreviewIcon] = useState<{ frameId: string; iconId: string } | null>(null);
  const [sampleImagesByFrameId, setSampleImagesByFrameId] = useState<Record<string, string[]>>({});
  const [frameDetailOpen, setFrameDetailOpen] = useState(false);
  const [frameDetailLoading, setFrameDetailLoading] = useState(false);
  const [frameDetailUploadingBackground, setFrameDetailUploadingBackground] = useState(false);
  const [frameDetailUploadingSamples, setFrameDetailUploadingSamples] = useState(false);
  const [deletingSampleId, setDeletingSampleId] = useState<string | null>(null);
  const [frameDetail, setFrameDetail] = useState<api.AlbumFrameDetailResponse | null>(null);
  const [frameDetailScope, setFrameDetailScope] = useState<{ albumId: string; frameId: string } | null>(null);
  const [detailFrameMeta, setDetailFrameMeta] = useState<api.CustomFrame | null>(null);
  const [sharedFrameBackgrounds, setSharedFrameBackgrounds] = useState<api.SharedFrameBackground[]>([]);
  const [sharedFrameIcons, setSharedFrameIcons] = useState<api.SharedFrameIcon[]>([]);
  const [loadingSharedFrameBackgrounds, setLoadingSharedFrameBackgrounds] = useState(false);
  const [loadingSharedFrameIcons, setLoadingSharedFrameIcons] = useState(false);
  const [uploadingSharedFrameBackground, setUploadingSharedFrameBackground] = useState(false);
  const [uploadingSharedFrameIcon, setUploadingSharedFrameIcon] = useState(false);
  const [deletingSharedBackgroundId, setDeletingSharedBackgroundId] = useState<string | null>(null);
  const [deletingSharedIconId, setDeletingSharedIconId] = useState<string | null>(null);
  const [sharedBackgroundPreviewErrorMap, setSharedBackgroundPreviewErrorMap] = useState<Record<string, boolean>>({});
  const [albumToDelete, setAlbumToDelete] = useState<api.PublicMenuAlbum | null>(null);
  const [sharedBackgroundToDelete, setSharedBackgroundToDelete] = useState<api.SharedFrameBackground | null>(null);
  const [confirmDeleting, setConfirmDeleting] = useState(false);
  const [coverCropOpen, setCoverCropOpen] = useState(false);
  const [coverCropSrc, setCoverCropSrc] = useState('');
  const [coverCropZoom, setCoverCropZoom] = useState(1);
  const [coverCropOffsetX, setCoverCropOffsetX] = useState(0);
  const [coverCropOffsetY, setCoverCropOffsetY] = useState(0);
  const [coverCropFileName, setCoverCropFileName] = useState('album-cover.jpg');
  const [coverCropApplying, setCoverCropApplying] = useState(false);
  const [coverCropDragging, setCoverCropDragging] = useState(false);
  const [frameBgCropOpen, setFrameBgCropOpen] = useState(false);
  const [frameBgCropSrc, setFrameBgCropSrc] = useState('');
  const [frameBgCropZoom, setFrameBgCropZoom] = useState(1);
  const [frameBgCropOffsetX, setFrameBgCropOffsetX] = useState(0);
  const [frameBgCropOffsetY, setFrameBgCropOffsetY] = useState(0);
  const [frameBgCropFileName, setFrameBgCropFileName] = useState('frame-background.jpg');
  const [frameBgCropApplying, setFrameBgCropApplying] = useState(false);
  const [frameBgCropDragging, setFrameBgCropDragging] = useState(false);
  const previewIframeRef = useRef<HTMLIFrameElement | null>(null);
  const backgroundCarouselRef = useRef<HTMLDivElement | null>(null);
  const selectedPagePreviewRef = useRef<HTMLDivElement | null>(null);
  const iconDragStateRef = useRef<{
    frameId: string;
    iconId: string;
    mode: 'move' | 'resize' | 'rotate';
    handle?: PreviewIconHandle;
    startX: number;
    startY: number;
    startXPct: number;
    startYPct: number;
    startSizePct: number;
    startRotateDeg: number;
    startPointerAngleDeg: number;
  } | null>(null);
  const COVER_PREVIEW_KEY = '__cover__';
  const coverCropCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const coverCropPointerStateRef = useRef<{
    pointerId: number | null;
    startX: number;
    startY: number;
    startOffsetX: number;
    startOffsetY: number;
  }>({
    pointerId: null,
    startX: 0,
    startY: 0,
    startOffsetX: 0,
    startOffsetY: 0,
  });
  const coverCropTouchStateRef = useRef<{
    mode: 'none' | 'drag' | 'pinch';
    startZoom: number;
    startDistance: number;
    dragStartX: number;
    dragStartY: number;
    dragStartOffsetX: number;
    dragStartOffsetY: number;
  }>({
    mode: 'none',
    startZoom: 1,
    startDistance: 0,
    dragStartX: 0,
    dragStartY: 0,
    dragStartOffsetX: 0,
    dragStartOffsetY: 0,
  });
  const frameBgCropCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const frameBgCropPointerStateRef = useRef<{
    pointerId: number | null;
    startX: number;
    startY: number;
    startOffsetX: number;
    startOffsetY: number;
  }>({
    pointerId: null,
    startX: 0,
    startY: 0,
    startOffsetX: 0,
    startOffsetY: 0,
  });
  const frameBgCropTouchStateRef = useRef<{
    mode: 'none' | 'drag' | 'pinch';
    startZoom: number;
    startDistance: number;
    dragStartX: number;
    dragStartY: number;
    dragStartOffsetX: number;
    dragStartOffsetY: number;
  }>({
    mode: 'none',
    startZoom: 1,
    startDistance: 0,
    dragStartX: 0,
    dragStartY: 0,
    dragStartOffsetX: 0,
    dragStartOffsetY: 0,
  });

  const loadAlbums = async (options?: { soft?: boolean }) => {
    try {
      if (!options?.soft) setLoading(true);
      const items = await api.getAdminPublicMenuAlbums();
      setAlbums(items);
    } catch (error) {
      console.error(error);
      toast.error('Không thể tải danh sách trang album');
    } finally {
      if (!options?.soft) setLoading(false);
    }
  };

  useEffect(() => {
    loadAlbums();
  }, []);

  useEffect(() => {
    const loadFrames = async () => {
      try {
        const frames = await api.getAdminPublicMenuFrames();
        setAvailableFrames(frames.filter((f) => Boolean(f.isActive)));
      } catch (error) {
        console.error(error);
        toast.error('Không thể tải danh sách custom frame');
      }
    };
    loadFrames();
  }, []);

  const loadSharedBackgrounds = useCallback(async () => {
    try {
      setLoadingSharedFrameBackgrounds(true);
      const items = await api.getSharedFrameBackgrounds();
      setSharedFrameBackgrounds(items);
      setSharedBackgroundPreviewErrorMap({});
    } catch (error) {
      console.error(error);
      toast.error('Không thể tải thư viện background');
    } finally {
      setLoadingSharedFrameBackgrounds(false);
    }
  }, []);

  useEffect(() => {
    loadSharedBackgrounds();
  }, [loadSharedBackgrounds]);

  const loadSharedIcons = useCallback(async () => {
    try {
      setLoadingSharedFrameIcons(true);
      const items = await api.getSharedFrameIcons();
      setSharedFrameIcons(items);
    } catch (error) {
      console.error(error);
      toast.error('Không thể tải thư viện icon');
    } finally {
      setLoadingSharedFrameIcons(false);
    }
  }, []);

  useEffect(() => {
    loadSharedIcons();
  }, [loadSharedIcons]);

  const createEmptyAlbumImmediately = async () => {
    try {
      setCreatingAlbum(true);
      await api.createPublicMenuAlbum({
        name: '',
        enabledFrameIds: [],
        isActive: true,
      });
      toast.success('Đã tạo trang album mới (rỗng). Bấm Sửa để cấu hình.');
      await loadAlbums({ soft: true });
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || 'Không thể tạo trang album');
    } finally {
      setCreatingAlbum(false);
    }
  };

  const openEdit = (album: api.PublicMenuAlbum) => {
    setEditing(album);
    setForm({
      name: album.name || '',
      slug: album.slug || '',
      coverImageUrl: album.coverImageUrl || '',
      coverIconImageUrl: album.coverIconImageUrl || '',
      backgroundImageUrl: album.backgroundImageUrl || '',
      isActive: Boolean(album.isActive),
    });
    setCoverFile(null);
    setBackgroundFile(null);
    setCoverPreviewUrl(album.coverImageUrl || '');
    setBackgroundPreviewUrl(album.backgroundImageUrl || '');
    setSelectedFrameIds(Array.isArray(album.enabledFrameIds) ? album.enabledFrameIds : []);
    setOrderDraftByFrameId({});
    setReorderingFrameId(null);
    const nextMainImageByFrameId: Record<string, string> = {};
    const nextFrameBackgroundByFrameId: Record<string, string> = {};
    const nextFrameIconByFrameId: Record<string, string> = {};
    const nextPreviewIconsByFrameId: Record<string, PreviewPageIcon[]> = {};
    const nextSampleImagesByFrameId: Record<string, string[]> = {};
    (album.frameConfigs || []).forEach((config) => {
      const frameId = String(config?.frameId || '').trim();
      const mainUrl = String(config?.mainImage?.url || '').trim();
      if (frameId && mainUrl) {
        nextMainImageByFrameId[frameId] = mainUrl;
      }
      const backgroundUrl = String(config?.backgroundImage?.url || '').trim();
      if (frameId && backgroundUrl) {
        nextFrameBackgroundByFrameId[frameId] = backgroundUrl;
      }
      const iconUrl = String(config?.iconImage?.url || '').trim();
      if (frameId && iconUrl) {
        nextFrameIconByFrameId[frameId] = iconUrl;
      }
      const iconPlacements = Array.isArray(config?.iconPlacements)
        ? config.iconPlacements
            .map((item) => {
              const url = String(item?.url || '').trim();
              if (!url) return null;
              return {
                id: `pi_seed_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                url,
                xPct: Number.isFinite(Number(item?.xPct)) ? Number(item?.xPct) : 50,
                yPct: Number.isFinite(Number(item?.yPct)) ? Number(item?.yPct) : 12,
                sizePct: Number.isFinite(Number(item?.sizePct)) ? Number(item?.sizePct) : 22,
                rotateDeg: Number.isFinite(Number(item?.rotateDeg)) ? Number(item?.rotateDeg) : 0,
              } as PreviewPageIcon;
            })
            .filter(Boolean) as PreviewPageIcon[]
        : [];
      if (frameId) {
        if (iconPlacements.length > 0) {
          nextPreviewIconsByFrameId[frameId] = iconPlacements;
        } else if (iconUrl) {
          // Backward-compatible fallback: dữ liệu cũ có iconImage nhưng chưa có iconPlacements.
          nextPreviewIconsByFrameId[frameId] = [
            {
              id: `pi_seed_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
              url: iconUrl,
              xPct: 50,
              yPct: 12,
              sizePct: 22,
              rotateDeg: 0,
            },
          ];
        }
      }
      if (frameId && Array.isArray(config?.sampleImages)) {
        const urls = config.sampleImages
          .map((item) => String(item?.url || '').trim())
          .filter(Boolean);
        if (urls.length > 0) {
          nextSampleImagesByFrameId[frameId] = urls;
        }
      }
    });
    setMainImageByFrameId(nextMainImageByFrameId);
    setFrameBackgroundByFrameId(nextFrameBackgroundByFrameId);
    setFrameIconByFrameId(nextFrameIconByFrameId);
    const coverIconPlacements = Array.isArray(album.coverIconPlacements)
      ? album.coverIconPlacements
          .map((item) => {
            const url = String(item?.url || '').trim();
            if (!url) return null;
            return {
              id: `pi_cover_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
              url,
              xPct: Number.isFinite(Number(item?.xPct)) ? Number(item?.xPct) : 50,
              yPct: Number.isFinite(Number(item?.yPct)) ? Number(item?.yPct) : 12,
              sizePct: Number.isFinite(Number(item?.sizePct)) ? Number(item?.sizePct) : 22,
              rotateDeg: Number.isFinite(Number(item?.rotateDeg)) ? Number(item?.rotateDeg) : 0,
            } as PreviewPageIcon;
          })
          .filter(Boolean) as PreviewPageIcon[]
      : [];
    if (coverIconPlacements.length > 0) {
      nextPreviewIconsByFrameId[COVER_PREVIEW_KEY] = coverIconPlacements;
    } else if (String(album.coverIconImageUrl || '').trim()) {
      nextPreviewIconsByFrameId[COVER_PREVIEW_KEY] = [
        {
          id: `pi_cover_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          url: String(album.coverIconImageUrl || '').trim(),
          xPct: 50,
          yPct: 12,
          sizePct: 22,
          rotateDeg: 0,
        },
      ];
    }
    setPreviewIconsByFrameId(nextPreviewIconsByFrameId);
    setSampleImagesByFrameId(nextSampleImagesByFrameId);
    setFrameDetail(null);
    setFrameDetailScope(null);
    setActivePreviewIcon(null);
    setPreviewZoom(1);
    setCenterPreviewMode('page');
    const firstEnabled =
      (album.enabledFrameIds || []).map(String).find((id) => Boolean(id)) ||
      (availableFrames[0]?._id ? String(availableFrames[0]._id) : null);
    setSelectedPageFrameId(firstEnabled);
    setOpen(true);
  };

  const closeDialog = () => {
    if (saving || uploadingCover || uploadingBackground) return;
    setActivePreviewIcon(null);
    setOpen(false);
  };

  useEffect(() => {
    if (!open) setActivePreviewIcon(null);
  }, [open]);

  useEffect(() => {
    setActivePreviewIcon(null);
  }, [selectedPageFrameId]);

  const previewUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (form.name.trim()) params.set('name', form.name.trim());
    if (form.coverImageUrl.trim()) params.set('cover', form.coverImageUrl.trim());
    if (form.coverIconImageUrl.trim()) params.set('coverIcon', form.coverIconImageUrl.trim());
    if (form.backgroundImageUrl.trim()) params.set('bg', form.backgroundImageUrl.trim());
    params.set('preview', '1');
    const query = params.toString();
    return `${PUBLIC_APP_ORIGIN}/public/custom-frame-menu${query ? `?${query}` : ''}`;
  }, [form.name, form.coverImageUrl, form.coverIconImageUrl, form.backgroundImageUrl]);

  const previewPayload = useMemo(
    () => ({
      name: form.name.trim(),
      coverImageUrl: (coverFile ? coverPreviewUrl : form.coverImageUrl).trim(),
      coverIconImageUrl: form.coverIconImageUrl.trim(),
      coverIconPlacements: (previewIconsByFrameId[COVER_PREVIEW_KEY] || []).map((item) => ({
        url: item.url,
        xPct: item.xPct,
        yPct: item.yPct,
        sizePct: item.sizePct,
        rotateDeg: item.rotateDeg,
      })),
      backgroundImageUrl: (backgroundFile ? backgroundPreviewUrl : form.backgroundImageUrl).trim(),
      enabledFrameIds: selectedFrameIds,
      mainImageByFrameId,
      frameBackgroundByFrameId,
      frameIconByFrameId,
      frameIconPlacementsByFrameId: Object.entries(previewIconsByFrameId).reduce<
        Record<string, Array<{ url: string; xPct: number; yPct: number; sizePct: number; rotateDeg: number }>>
      >((acc, [frameId, icons]) => {
        if (!frameId || frameId === COVER_PREVIEW_KEY) return acc;
        if (!Array.isArray(icons) || icons.length === 0) {
          acc[frameId] = [];
          return acc;
        }
        acc[frameId] = icons.map((item) => ({
          url: item.url,
          xPct: item.xPct,
          yPct: item.yPct,
          sizePct: item.sizePct,
          rotateDeg: item.rotateDeg,
        }));
        return acc;
      }, selectedFrameIds.reduce<Record<string, Array<{ url: string; xPct: number; yPct: number; sizePct: number; rotateDeg: number }>>>((acc, frameId) => {
        const normalized = String(frameId || '').trim();
        if (normalized) acc[normalized] = [];
        return acc;
      }, {})),
      sampleImagesByFrameId,
      zoom: previewZoom,
    }),
    [
      form.name,
      form.coverImageUrl,
      form.coverIconImageUrl,
      form.backgroundImageUrl,
      coverFile,
      backgroundFile,
      coverPreviewUrl,
      backgroundPreviewUrl,
      selectedFrameIds,
      mainImageByFrameId,
      frameBackgroundByFrameId,
      frameIconByFrameId,
      previewIconsByFrameId,
      sampleImagesByFrameId,
      previewZoom,
    ]
  );

  const copyText = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Đã copy ${label}`);
    } catch {
      toast.error('Không copy được');
    }
  };

  const fileToDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Không đọc được file ảnh'));
      reader.readAsDataURL(file);
    });

  const loadImageElement = (src: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Không thể đọc ảnh'));
      img.src = src;
    });

  const drawCoverCropPreview = useCallback(
    async (src: string, zoom: number, panX: number, panY: number) => {
      const canvas = coverCropCanvasRef.current;
      if (!canvas || !src) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const image = await loadImageElement(src);

      const cw = FRAME_BG_CROP_WIDTH;
      const ch = FRAME_BG_CROP_HEIGHT;
      canvas.width = cw;
      canvas.height = ch;

      const baseScale = Math.max(cw / image.naturalWidth, ch / image.naturalHeight);
      const scale = baseScale * Math.max(FRAME_BG_ZOOM_MIN, Math.min(FRAME_BG_ZOOM_MAX, zoom));
      const drawW = image.naturalWidth * scale;
      const drawH = image.naturalHeight * scale;
      const extraX = Math.max(0, drawW - cw);
      const extraY = Math.max(0, drawH - ch);
      const safePanX = Math.max(-FRAME_BG_PAN_LIMIT, Math.min(FRAME_BG_PAN_LIMIT, panX));
      const safePanY = Math.max(-FRAME_BG_PAN_LIMIT, Math.min(FRAME_BG_PAN_LIMIT, panY));

      const dx = (cw - drawW) / 2 - (safePanX / 100) * (extraX / 2);
      const dy = (ch - drawH) / 2 - (safePanY / 100) * (extraY / 2);

      ctx.clearRect(0, 0, cw, ch);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(image, dx, dy, drawW, drawH);
    },
    []
  );

  const drawFrameBgCropPreview = useCallback(
    async (src: string, zoom: number, panX: number, panY: number) => {
      const canvas = frameBgCropCanvasRef.current;
      if (!canvas || !src) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const image = await loadImageElement(src);

      const cw = FRAME_BG_CROP_WIDTH;
      const ch = FRAME_BG_CROP_HEIGHT;
      canvas.width = cw;
      canvas.height = ch;

      const baseScale = Math.max(cw / image.naturalWidth, ch / image.naturalHeight);
      const scale = baseScale * Math.max(FRAME_BG_ZOOM_MIN, Math.min(FRAME_BG_ZOOM_MAX, zoom));
      const drawW = image.naturalWidth * scale;
      const drawH = image.naturalHeight * scale;
      const extraX = Math.max(0, drawW - cw);
      const extraY = Math.max(0, drawH - ch);
      const safePanX = Math.max(-FRAME_BG_PAN_LIMIT, Math.min(FRAME_BG_PAN_LIMIT, panX));
      const safePanY = Math.max(-FRAME_BG_PAN_LIMIT, Math.min(FRAME_BG_PAN_LIMIT, panY));

      const dx = (cw - drawW) / 2 - (safePanX / 100) * (extraX / 2);
      const dy = (ch - drawH) / 2 - (safePanY / 100) * (extraY / 2);

      ctx.clearRect(0, 0, cw, ch);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(image, dx, dy, drawW, drawH);
    },
    []
  );

  const clampFrameBgPan = useCallback(
    (value: number) => Math.max(-FRAME_BG_PAN_LIMIT, Math.min(FRAME_BG_PAN_LIMIT, value)),
    []
  );

  const createCroppedFrameBackgroundFile = useCallback(
    async (src: string, zoom: number, panX: number, panY: number, fileName: string) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Không thể xử lý ảnh');
      const image = await loadImageElement(src);

      const cw = FRAME_BG_CROP_WIDTH;
      const ch = FRAME_BG_CROP_HEIGHT;
      canvas.width = cw;
      canvas.height = ch;

      const baseScale = Math.max(cw / image.naturalWidth, ch / image.naturalHeight);
      const scale = baseScale * Math.max(FRAME_BG_ZOOM_MIN, Math.min(FRAME_BG_ZOOM_MAX, zoom));
      const drawW = image.naturalWidth * scale;
      const drawH = image.naturalHeight * scale;
      const extraX = Math.max(0, drawW - cw);
      const extraY = Math.max(0, drawH - ch);
      const safePanX = Math.max(-FRAME_BG_PAN_LIMIT, Math.min(FRAME_BG_PAN_LIMIT, panX));
      const safePanY = Math.max(-FRAME_BG_PAN_LIMIT, Math.min(FRAME_BG_PAN_LIMIT, panY));

      const dx = (cw - drawW) / 2 - (safePanX / 100) * (extraX / 2);
      const dy = (ch - drawH) / 2 - (safePanY / 100) * (extraY / 2);

      ctx.clearRect(0, 0, cw, ch);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(image, dx, dy, drawW, drawH);

      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
      if (!blob) throw new Error('Không thể tạo ảnh sau khi cắt');
      return new File([blob], fileName || 'frame-background.jpg', { type: 'image/jpeg' });
    },
    []
  );

  const openFrameBackgroundCropper = async (file?: File) => {
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setFrameBgCropSrc(dataUrl);
      setFrameBgCropFileName(file.name || 'frame-background.jpg');
      setFrameBgCropZoom(1);
      setFrameBgCropOffsetX(0);
      setFrameBgCropOffsetY(0);
      setFrameBgCropOpen(true);
    } catch {
      toast.error('Không đọc được file ảnh');
    }
  };

  const handleSelectImage = async (file: File | undefined, type: 'cover' | 'background') => {
    if (!file) return;
    if (type === 'cover') {
      await openCoverCropper(file);
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setBackgroundFile(file);
      setBackgroundPreviewUrl(dataUrl);
    } catch {
      toast.error('Không đọc được file ảnh');
    }
  };

  const handleClearImage = (type: 'cover' | 'background') => {
    if (type === 'cover') {
      setCoverFile(null);
      setCoverPreviewUrl('');
      setForm((prev) => ({ ...prev, coverImageUrl: '' }));
      return;
    }
    setBackgroundFile(null);
    setBackgroundPreviewUrl('');
    setForm((prev) => ({ ...prev, backgroundImageUrl: '' }));
  };

  const uploadCoverIconToLibrary = async (file?: File) => {
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error('Icon phải nhỏ hơn 500KB');
      return;
    }
    try {
      setUploadingSharedFrameIcon(true);
      const uploaded = await api.uploadSharedFrameIcon(file);
      await loadSharedIcons();
      const nextUrl = String(uploaded.icon?.url || '').trim();
      setForm((prev) => ({ ...prev, coverIconImageUrl: nextUrl || prev.coverIconImageUrl }));
      if (nextUrl) {
        setPreviewIconsByFrameId((prev) => ({
          ...prev,
          [COVER_PREVIEW_KEY]: [
            {
              id: `pi_cover_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
              url: nextUrl,
              xPct: 50,
              yPct: 12,
              sizePct: 22,
              rotateDeg: 0,
            },
          ],
        }));
      }
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || 'Không thể upload icon bìa');
    } finally {
      setUploadingSharedFrameIcon(false);
    }
  };

  useEffect(() => {
    if (!coverCropOpen || !coverCropSrc) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const drawWhenReady = async (attempt = 0) => {
      if (cancelled) return;
      if (!coverCropCanvasRef.current) {
        if (attempt < 12) {
          timer = setTimeout(() => {
            drawWhenReady(attempt + 1).catch(() => {});
          }, 35);
        }
        return;
      }
      try {
        await drawCoverCropPreview(coverCropSrc, coverCropZoom, coverCropOffsetX, coverCropOffsetY);
      } catch {
        if (!cancelled) toast.error('Không thể hiển thị preview cắt ảnh bìa');
      }
    };

    drawWhenReady().catch(() => {});
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [coverCropOpen, coverCropSrc, coverCropZoom, coverCropOffsetX, coverCropOffsetY, drawCoverCropPreview]);

  const openCoverCropper = async (file?: File) => {
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setCoverCropSrc(dataUrl);
      setCoverCropFileName(file.name || 'album-cover.jpg');
      setCoverCropZoom(1);
      setCoverCropOffsetX(0);
      setCoverCropOffsetY(0);
      setCoverCropOpen(true);
    } catch {
      toast.error('Không đọc được file ảnh');
    }
  };

  const closeCoverCropper = () => {
    if (coverCropApplying) return;
    setCoverCropOpen(false);
    setCoverCropSrc('');
    setCoverCropZoom(1);
    setCoverCropOffsetX(0);
    setCoverCropOffsetY(0);
    setCoverCropFileName('album-cover.jpg');
    setCoverCropDragging(false);
    coverCropPointerStateRef.current.pointerId = null;
    coverCropTouchStateRef.current.mode = 'none';
  };

  const handleCoverCropPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (coverCropApplying) return;
    if (event.pointerType === 'touch') return;
    event.preventDefault();
    coverCropPointerStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startOffsetX: coverCropOffsetX,
      startOffsetY: coverCropOffsetY,
    };
    setCoverCropDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleCoverCropPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = coverCropPointerStateRef.current;
    if (!coverCropDragging || state.pointerId !== event.pointerId) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const dxPercent = rect.width > 0 ? ((event.clientX - state.startX) / rect.width) * 100 : 0;
    const dyPercent = rect.height > 0 ? ((event.clientY - state.startY) / rect.height) * 100 : 0;
    setCoverCropOffsetX(clampFrameBgPan(state.startOffsetX - dxPercent));
    setCoverCropOffsetY(clampFrameBgPan(state.startOffsetY - dyPercent));
  };

  const handleCoverCropPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = coverCropPointerStateRef.current;
    if (state.pointerId !== null && state.pointerId === event.pointerId) {
      setCoverCropDragging(false);
      coverCropPointerStateRef.current.pointerId = null;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
  };

  const handleCoverCropTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (coverCropApplying) return;
    if (event.touches.length === 2) {
      const [a, b] = [event.touches[0], event.touches[1]];
      const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      coverCropTouchStateRef.current = {
        ...coverCropTouchStateRef.current,
        mode: 'pinch',
        startZoom: coverCropZoom,
        startDistance: distance,
      };
      setCoverCropDragging(false);
    } else if (event.touches.length === 1) {
      coverCropTouchStateRef.current = {
        ...coverCropTouchStateRef.current,
        mode: 'drag',
        dragStartX: event.touches[0].clientX,
        dragStartY: event.touches[0].clientY,
        dragStartOffsetX: coverCropOffsetX,
        dragStartOffsetY: coverCropOffsetY,
      };
      setCoverCropDragging(true);
    }
  };

  const handleCoverCropTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (coverCropApplying) return;
    const touchState = coverCropTouchStateRef.current;
    const rect = event.currentTarget.getBoundingClientRect();

    if (event.touches.length === 2 && touchState.mode === 'pinch') {
      event.preventDefault();
      const [a, b] = [event.touches[0], event.touches[1]];
      const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      if (!touchState.startDistance) return;
      const ratio = distance / touchState.startDistance;
      const nextZoom = Math.max(FRAME_BG_ZOOM_MIN, Math.min(FRAME_BG_ZOOM_MAX, touchState.startZoom * ratio));
      setCoverCropZoom(nextZoom);
      return;
    }

    if (event.touches.length === 1 && touchState.mode === 'drag') {
      event.preventDefault();
      const dxPercent = rect.width > 0 ? ((event.touches[0].clientX - touchState.dragStartX) / rect.width) * 100 : 0;
      const dyPercent = rect.height > 0 ? ((event.touches[0].clientY - touchState.dragStartY) / rect.height) * 100 : 0;
      setCoverCropOffsetX(clampFrameBgPan(touchState.dragStartOffsetX - dxPercent));
      setCoverCropOffsetY(clampFrameBgPan(touchState.dragStartOffsetY - dyPercent));
    }
  };

  const handleCoverCropTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length === 0) {
      coverCropTouchStateRef.current.mode = 'none';
      setCoverCropDragging(false);
    } else if (event.touches.length === 1) {
      coverCropTouchStateRef.current = {
        ...coverCropTouchStateRef.current,
        mode: 'drag',
        dragStartX: event.touches[0].clientX,
        dragStartY: event.touches[0].clientY,
        dragStartOffsetX: coverCropOffsetX,
        dragStartOffsetY: coverCropOffsetY,
      };
      setCoverCropDragging(true);
    }
  };

  const applyCoverCrop = async () => {
    if (!coverCropSrc) return;
    try {
      setCoverCropApplying(true);
      const croppedFile = await createCroppedFrameBackgroundFile(
        coverCropSrc,
        coverCropZoom,
        coverCropOffsetX,
        coverCropOffsetY,
        coverCropFileName || 'album-cover.jpg'
      );
      const previewDataUrl = await fileToDataUrl(croppedFile);
      setCoverFile(croppedFile);
      setCoverPreviewUrl(previewDataUrl);
      closeCoverCropper();
    } catch {
      toast.error('Không thể cắt ảnh bìa');
    } finally {
      setCoverCropApplying(false);
    }
  };

  const updatePreviewZoom = useCallback((delta: number) => {
    setPreviewZoom((prev) => {
      const next = Math.round((prev + delta) * 100) / 100;
      return Math.max(MIN_PREVIEW_ZOOM, Math.min(MAX_PREVIEW_ZOOM, next));
    });
  }, []);

  const resetPreviewZoom = useCallback(() => {
    setPreviewZoom(1);
  }, []);

  const pushPreviewToIframe = useCallback(() => {
    const frame = previewIframeRef.current;
    const target = frame?.contentWindow;
    if (!target) return;
    target.postMessage(
      {
        type: 'PUBLIC_ALBUM_PREVIEW',
        payload: previewPayload,
      },
      '*'
    );
  }, [previewPayload]);

  useEffect(() => {
    if (!open) return;
    pushPreviewToIframe();
  }, [open, pushPreviewToIframe]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const isZoomCombo = event.ctrlKey || event.metaKey;
      if (!isZoomCombo) return;
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        updatePreviewZoom(PREVIEW_ZOOM_STEP);
        return;
      }
      if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        updatePreviewZoom(-PREVIEW_ZOOM_STEP);
        return;
      }
      if (event.key === '0') {
        event.preventDefault();
        resetPreviewZoom();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, resetPreviewZoom, updatePreviewZoom]);

  useEffect(() => {
    if (!frameBgCropOpen || !frameBgCropSrc) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const drawWhenReady = async (attempt = 0) => {
      if (cancelled) return;
      if (!frameBgCropCanvasRef.current) {
        if (attempt < 12) {
          timer = setTimeout(() => {
            drawWhenReady(attempt + 1).catch(() => {});
          }, 35);
        }
        return;
      }
      try {
        await drawFrameBgCropPreview(frameBgCropSrc, frameBgCropZoom, frameBgCropOffsetX, frameBgCropOffsetY);
      } catch {
        if (!cancelled) toast.error('Không thể hiển thị preview cắt ảnh');
      }
    };

    drawWhenReady().catch(() => {});
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [frameBgCropOpen, frameBgCropSrc, frameBgCropZoom, frameBgCropOffsetX, frameBgCropOffsetY, drawFrameBgCropPreview]);

  const closeFrameBackgroundCropper = () => {
    if (frameBgCropApplying) return;
    setFrameBgCropOpen(false);
    setFrameBgCropSrc('');
    setFrameBgCropZoom(1);
    setFrameBgCropOffsetX(0);
    setFrameBgCropOffsetY(0);
    setFrameBgCropFileName('frame-background.jpg');
    setFrameBgCropDragging(false);
    frameBgCropPointerStateRef.current.pointerId = null;
    frameBgCropTouchStateRef.current.mode = 'none';
  };

  const handleFrameBgCropPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (frameBgCropApplying) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    frameBgCropPointerStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startOffsetX: frameBgCropOffsetX,
      startOffsetY: frameBgCropOffsetY,
    };
    setFrameBgCropDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleFrameBgCropPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = frameBgCropPointerStateRef.current;
    if (!frameBgCropDragging || state.pointerId !== event.pointerId) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const deltaXPercent = rect.width > 0 ? ((event.clientX - state.startX) / rect.width) * 100 : 0;
    const deltaYPercent = rect.height > 0 ? ((event.clientY - state.startY) / rect.height) * 100 : 0;
    setFrameBgCropOffsetX(clampFrameBgPan(state.startOffsetX - deltaXPercent));
    setFrameBgCropOffsetY(clampFrameBgPan(state.startOffsetY - deltaYPercent));
  };

  const handleFrameBgCropPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = frameBgCropPointerStateRef.current;
    if (state.pointerId === event.pointerId) {
      setFrameBgCropDragging(false);
      frameBgCropPointerStateRef.current.pointerId = null;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
  };

  const getTouchDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleFrameBgCropTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (frameBgCropApplying) return;
    if (event.touches.length >= 2) {
      frameBgCropTouchStateRef.current = {
        ...frameBgCropTouchStateRef.current,
        mode: 'pinch',
        startZoom: frameBgCropZoom,
        startDistance: getTouchDistance(event.touches),
      };
      return;
    }
    if (event.touches.length === 1) {
      frameBgCropTouchStateRef.current = {
        ...frameBgCropTouchStateRef.current,
        mode: 'drag',
        dragStartX: event.touches[0].clientX,
        dragStartY: event.touches[0].clientY,
        dragStartOffsetX: frameBgCropOffsetX,
        dragStartOffsetY: frameBgCropOffsetY,
      };
      setFrameBgCropDragging(true);
    }
  };

  const handleFrameBgCropTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (frameBgCropApplying) return;
    const touchState = frameBgCropTouchStateRef.current;
    const rect = event.currentTarget.getBoundingClientRect();

    if (event.touches.length >= 2 || touchState.mode === 'pinch') {
      event.preventDefault();
      const currentDistance = getTouchDistance(event.touches);
      if (touchState.startDistance <= 0) return;
      const ratio = currentDistance / touchState.startDistance;
      const nextZoom = Math.max(FRAME_BG_ZOOM_MIN, Math.min(FRAME_BG_ZOOM_MAX, touchState.startZoom * ratio));
      setFrameBgCropZoom(nextZoom);
      return;
    }

    if (event.touches.length === 1 && touchState.mode === 'drag') {
      event.preventDefault();
      const dxPercent = rect.width > 0 ? ((event.touches[0].clientX - touchState.dragStartX) / rect.width) * 100 : 0;
      const dyPercent = rect.height > 0 ? ((event.touches[0].clientY - touchState.dragStartY) / rect.height) * 100 : 0;
      setFrameBgCropOffsetX(clampFrameBgPan(touchState.dragStartOffsetX - dxPercent));
      setFrameBgCropOffsetY(clampFrameBgPan(touchState.dragStartOffsetY - dyPercent));
    }
  };

  const handleFrameBgCropTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length === 0) {
      frameBgCropTouchStateRef.current.mode = 'none';
      setFrameBgCropDragging(false);
    } else if (event.touches.length === 1) {
      frameBgCropTouchStateRef.current = {
        ...frameBgCropTouchStateRef.current,
        mode: 'drag',
        dragStartX: event.touches[0].clientX,
        dragStartY: event.touches[0].clientY,
        dragStartOffsetX: frameBgCropOffsetX,
        dragStartOffsetY: frameBgCropOffsetY,
      };
      setFrameBgCropDragging(true);
    }
  };

  const applyFrameBackgroundCrop = async () => {
    if (!frameBgCropSrc || !editing?._id || !detailFrameMeta?._id) return;
    try {
      setFrameBgCropApplying(true);
      const croppedFile = await createCroppedFrameBackgroundFile(
        frameBgCropSrc,
        frameBgCropZoom,
        frameBgCropOffsetX,
        frameBgCropOffsetY,
        frameBgCropFileName
      );
      await uploadBackgroundImage(croppedFile);
      closeFrameBackgroundCropper();
    } catch {
      toast.error('Không thể cắt ảnh background');
    } finally {
      setFrameBgCropApplying(false);
    }
  };

  const submit = async () => {
    const slugBase = form.slug || form.name || (editing ? editing.slug : '');
    const slug = toSlug(slugBase) || generateAutoSlug();
    if (!slug) {
      toast.error('Slug không hợp lệ');
      return;
    }
    const albumName = form.name.trim() || `Album ${slug}`;

    try {
      setSaving(true);
      let coverImageUrl = form.coverImageUrl.trim();
      const coverIconImageUrl = form.coverIconImageUrl.trim();
      const coverIconPlacements = (previewIconsByFrameId[COVER_PREVIEW_KEY] || []).map((item) => ({
        url: item.url,
        xPct: item.xPct,
        yPct: item.yPct,
        sizePct: item.sizePct,
        rotateDeg: item.rotateDeg,
      }));
      const frameIconPlacementsByFrameId = Object.entries(previewIconsByFrameId).reduce<
        Record<string, Array<{ url: string; xPct: number; yPct: number; sizePct: number; rotateDeg: number }>>
      >((acc, [frameId, icons]) => {
        if (!frameId || frameId === COVER_PREVIEW_KEY) return acc;
        if (!Array.isArray(icons) || icons.length === 0) {
          acc[frameId] = [];
          return acc;
        }
        acc[frameId] = icons.map((item) => ({
          url: item.url,
          xPct: item.xPct,
          yPct: item.yPct,
          sizePct: item.sizePct,
          rotateDeg: item.rotateDeg,
        }));
        return acc;
      }, selectedFrameIds.reduce<Record<string, Array<{ url: string; xPct: number; yPct: number; sizePct: number; rotateDeg: number }>>>((acc, frameId) => {
        const normalized = String(frameId || '').trim();
        if (normalized) acc[normalized] = [];
        return acc;
      }, {}));
      let backgroundImageUrl = form.backgroundImageUrl.trim();

      if (coverFile) {
        setUploadingCover(true);
        const uploaded = await api.uploadPublicMenuAlbumImage(coverFile, 'cover');
        coverImageUrl = uploaded.image?.url || coverImageUrl;
        setUploadingCover(false);
      }
      if (backgroundFile) {
        setUploadingBackground(true);
        const uploaded = await api.uploadPublicMenuAlbumImage(backgroundFile, 'background');
        backgroundImageUrl = uploaded.image?.url || backgroundImageUrl;
        setUploadingBackground(false);
      }

      if (editing) {
        await api.updatePublicMenuAlbum(editing._id, {
          name: albumName,
          slug,
          coverImageUrl,
          coverIconImageUrl,
          coverIconPlacements,
          backgroundImageUrl,
          frameIconPlacementsByFrameId,
          enabledFrameIds: selectedFrameIds,
          isActive: form.isActive,
        });
        toast.success('Đã cập nhật trang album');
      } else {
        await api.createPublicMenuAlbum({
          name: albumName,
          slug,
          coverImageUrl,
          coverIconImageUrl,
          coverIconPlacements,
          backgroundImageUrl,
          frameIconPlacementsByFrameId,
          enabledFrameIds: selectedFrameIds,
          isActive: form.isActive,
        });
        toast.success('Đã tạo trang album mới');
      }

      setOpen(false);
      await loadAlbums();
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || 'Không thể lưu trang album');
    } finally {
      setSaving(false);
      setUploadingCover(false);
      setUploadingBackground(false);
    }
  };

  const removeAlbum = async (album: api.PublicMenuAlbum) => {
    setAlbumToDelete(album);
  };

  const confirmRemoveAlbum = async () => {
    if (!albumToDelete?._id) return;
    try {
      setConfirmDeleting(true);
      await api.deletePublicMenuAlbum(albumToDelete._id);
      toast.success('Đã xóa trang album');
      await loadAlbums();
      setAlbumToDelete(null);
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || 'Không thể xóa trang album');
    } finally {
      setConfirmDeleting(false);
    }
  };

  const toggleActive = async (album: api.PublicMenuAlbum, next: boolean) => {
    try {
      await api.updatePublicMenuAlbum(album._id, { isActive: next });
      setAlbums((prev) => prev.map((x) => (x._id === album._id ? { ...x, isActive: next } : x)));
      toast.success(next ? 'Đã bật trang album' : 'Đã tắt trang album');
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || 'Không thể cập nhật trạng thái');
    }
  };

  const sortedFrames = useMemo(() => {
    const priorityMap = new Map(FRAME_SIZE_PRIORITY.map((size, idx) => [normalizeFrameSize(size), idx]));
    return [...availableFrames].sort((a, b) => {
      const aSize = normalizeFrameSize(a.size);
      const bSize = normalizeFrameSize(b.size);
      const aPriority = priorityMap.has(aSize) ? priorityMap.get(aSize)! : Number.MAX_SAFE_INTEGER;
      const bPriority = priorityMap.has(bSize) ? priorityMap.get(bSize)! : Number.MAX_SAFE_INTEGER;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [availableFrames]);

  const coverPagePreviewUrl = useMemo(
    () => ((coverFile ? coverPreviewUrl : form.coverImageUrl) || '').trim(),
    [coverFile, coverPreviewUrl, form.coverImageUrl]
  );

  const selectedPageFrame = useMemo(
    () => (selectedPageFrameId ? sortedFrames.find((frame) => frame._id === selectedPageFrameId) || null : null),
    [selectedPageFrameId, sortedFrames]
  );

  const frameDetailMatchesSelectedPage = useMemo(() => {
    if (!editing?._id || !selectedPageFrame?._id || !frameDetailScope) return false;
    return (
      frameDetailScope.albumId === editing._id &&
      frameDetailScope.frameId === String(selectedPageFrame._id).trim()
    );
  }, [editing?._id, selectedPageFrame, frameDetailScope]);

  const frameDetailMatchesDialogFrame = useMemo(() => {
    if (!editing?._id || !detailFrameMeta?._id || !frameDetailScope) return false;
    return (
      frameDetailScope.albumId === editing._id &&
      frameDetailScope.frameId === String(detailFrameMeta._id).trim()
    );
  }, [editing?._id, detailFrameMeta?._id, frameDetailScope]);

  const scopedSampleImagesForSelectedPage = frameDetailMatchesSelectedPage
    ? frameDetail?.config?.sampleImages || []
    : [];

  const scopedSampleImagesForDialog = frameDetailMatchesDialogFrame ? frameDetail?.config?.sampleImages || [] : [];

  const orderedFrameSizes = useMemo(() => {
    const availableSizes = new Set(
      sortedFrames.map((frame) => normalizeFrameSize(frame.size)).filter(Boolean)
    );
    const orderedByPriority = FRAME_SIZE_PRIORITY.map((size) => normalizeFrameSize(size));
    const remaining = Array.from(availableSizes)
      .filter((size) => !orderedByPriority.includes(size))
      .sort((a, b) => a.localeCompare(b));
    return [...orderedByPriority.filter((size) => availableSizes.has(size)), ...remaining];
  }, [sortedFrames]);

  /** Enabled pages follow `selectedFrameIds` order (manual STT), not size sort. */
  const enabledFrames = useMemo(() => {
    const byId = new Map(sortedFrames.map((frame) => [frame._id, frame]));
    return selectedFrameIds
      .map((id) => byId.get(id))
      .filter((frame): frame is api.CustomFrame => Boolean(frame));
  }, [sortedFrames, selectedFrameIds]);

  const disabledFrames = useMemo(
    () => sortedFrames.filter((frame) => !selectedFrameIds.includes(frame._id)),
    [sortedFrames, selectedFrameIds]
  );

  /** Enabled frames first, then disabled — for the page list sidebar. */
  const pageListFrames = useMemo(() => {
    const all = [...enabledFrames, ...disabledFrames];
    if (selectedFrameSizes.length === 0) return all;
    const sizeSet = new Set(selectedFrameSizes.map(normalizeFrameSize));
    return all.filter((frame) => sizeSet.has(normalizeFrameSize(frame.size)));
  }, [enabledFrames, disabledFrames, selectedFrameSizes]);

  const toggleFrameSizeFilter = (size: string) => {
    setSelectedFrameSizes((prev) =>
      prev.includes(size) ? prev.filter((item) => item !== size) : [...prev, size]
    );
  };

  useEffect(() => {
    if (!selectedPageFrameId) return;
    const stillExists = sortedFrames.some((frame) => frame._id === selectedPageFrameId);
    if (!stillExists) {
      setSelectedPageFrameId(selectedFrameIds[0] || sortedFrames[0]?._id || null);
    }
  }, [selectedPageFrameId, selectedFrameIds, sortedFrames]);

  const refreshFrameDetail = useCallback(
    async (
      albumId: string,
      frameId: string,
      opts?: { pruneSampleKeysToFrameIds?: string[] }
    ) => {
      const detail = await api.getAlbumFrameDetail(albumId, frameId);
      setFrameDetail(detail);
      const normalizedFrameId = String(detail.config?.frameId || frameId).trim();
      setFrameDetailScope({
        albumId: String(albumId || '').trim(),
        frameId: normalizedFrameId,
      });
      const normalizedMainUrl = String(detail.config?.mainImage?.url || '').trim();
      setMainImageByFrameId((prev) => {
        const next = { ...prev };
        if (normalizedMainUrl) next[normalizedFrameId] = normalizedMainUrl;
        else delete next[normalizedFrameId];
        return next;
      });
      const normalizedBackgroundUrl = String(detail.config?.backgroundImage?.url || '').trim();
      setFrameBackgroundByFrameId((prev) => {
        const next = { ...prev };
        if (normalizedBackgroundUrl) next[normalizedFrameId] = normalizedBackgroundUrl;
        else delete next[normalizedFrameId];
        return next;
      });
      const normalizedIconUrl = String(detail.config?.iconImage?.url || '').trim();
      setFrameIconByFrameId((prev) => {
        const next = { ...prev };
        if (normalizedIconUrl) next[normalizedFrameId] = normalizedIconUrl;
        else delete next[normalizedFrameId];
        return next;
      });
      setPreviewIconsByFrameId((prev) => {
        const hasLocalState = Object.prototype.hasOwnProperty.call(prev, normalizedFrameId);
        const placements = Array.isArray(detail.config?.iconPlacements)
          ? detail.config.iconPlacements
              .map((item) => {
                const url = String(item?.url || '').trim();
                if (!url) return null;
                return {
                  id: `pi_seed_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                  url,
                  xPct: Number.isFinite(Number(item?.xPct)) ? Number(item?.xPct) : 50,
                  yPct: Number.isFinite(Number(item?.yPct)) ? Number(item?.yPct) : 12,
                  sizePct: Number.isFinite(Number(item?.sizePct)) ? Number(item?.sizePct) : 22,
                  rotateDeg: Number.isFinite(Number(item?.rotateDeg)) ? Number(item?.rotateDeg) : 0,
                } as PreviewPageIcon;
              })
              .filter(Boolean) as PreviewPageIcon[]
          : [];
        const existing = prev[normalizedFrameId] || [];
        if (placements.length > 0) {
          // Ưu tiên state local (kể cả [] khi user vừa xóa icon nhưng chưa lưu).
          if (hasLocalState) return prev;
          return { ...prev, [normalizedFrameId]: placements };
        }
        if (normalizedIconUrl) {
          // Tránh backend seed lại icon cũ khi local đang chủ ý để rỗng.
          if (hasLocalState) return prev;
          return {
            ...prev,
            [normalizedFrameId]: [
              {
                id: `pi_seed_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                url: normalizedIconUrl,
                xPct: 50,
                yPct: 12,
                sizePct: 22,
                rotateDeg: 0,
              },
            ],
          };
        }
        if (!hasLocalState) return prev;
        const next = { ...prev };
        delete next[normalizedFrameId];
        return next;
      });
      const normalizedSamples = Array.isArray(detail.config?.sampleImages)
        ? detail.config.sampleImages.map((item) => String(item?.url || '').trim()).filter(Boolean)
        : [];
      setSampleImagesByFrameId((prev) => {
        const next = { ...prev };
        if (normalizedSamples.length > 0) next[normalizedFrameId] = normalizedSamples;
        else delete next[normalizedFrameId];
        const pruneTo = opts?.pruneSampleKeysToFrameIds;
        if (Array.isArray(pruneTo) && pruneTo.length > 0) {
          const allow = new Set(pruneTo.map((id) => String(id || '').trim()).filter(Boolean));
          Object.keys(next).forEach((k) => {
            if (!allow.has(k)) delete next[k];
          });
        }
        return next;
      });
    },
    []
  );

  useEffect(() => {
    if (!open || !editing?._id || !selectedPageFrame?._id) return;
    let cancelled = false;
    setDetailFrameMeta(selectedPageFrame);
    setFrameDetail(null);
    setFrameDetailScope(null);
    setFrameDetailLoading(true);
    refreshFrameDetail(editing._id, selectedPageFrame._id, { pruneSampleKeysToFrameIds: selectedFrameIds })
      .catch((error: unknown) => {
        if (cancelled) return;
        const msg =
          error && typeof error === 'object' && 'response' in error
            ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
            : undefined;
        toast.error(msg || 'Không thể tải chi tiết custom frame');
      })
      .finally(() => {
        if (!cancelled) setFrameDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, editing?._id, selectedPageFrame, selectedFrameIds, refreshFrameDetail]);

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      const state = iconDragStateRef.current;
      if (!state) return;
      const node = selectedPagePreviewRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const dxPct = ((event.clientX - state.startX) / rect.width) * 100;
      const dyPct = ((event.clientY - state.startY) / rect.height) * 100;
      setPreviewIconsByFrameId((prev) => {
        const list = prev[state.frameId] || [];
        const nextList = list.map((item) => {
          if (item.id !== state.iconId) return item;
          if (state.mode === 'move') {
            const nextX = Math.max(5, Math.min(95, state.startXPct + dxPct));
            const nextY = Math.max(5, Math.min(95, state.startYPct + dyPct));
            return { ...item, xPct: nextX, yPct: nextY };
          }
          if (state.mode === 'rotate') {
            const centerX = rect.left + (state.startXPct / 100) * rect.width;
            const centerY = rect.top + (state.startYPct / 100) * rect.height;
            const currentAngleDeg = (Math.atan2(event.clientY - centerY, event.clientX - centerX) * 180) / Math.PI;
            const nextRotateDeg = normalizeRotationDeg(state.startRotateDeg + (currentAngleDeg - state.startPointerAngleDeg));
            return { ...item, rotateDeg: nextRotateDeg };
          }
          const dxByWidthPct = ((event.clientX - state.startX) / rect.width) * 100;
          const dyByWidthPct = ((event.clientY - state.startY) / rect.width) * 100;
          let sizeDelta = 0;
          switch (state.handle) {
            case 'tl':
              sizeDelta = (-dxByWidthPct - dyByWidthPct) * 0.5;
              break;
            case 'br':
            default:
              sizeDelta = (dxByWidthPct + dyByWidthPct) * 0.5;
              break;
          }
          const nextSizePct = Math.max(8, Math.min(55, state.startSizePct + sizeDelta));
          return { ...item, sizePct: nextSizePct };
        });
        return { ...prev, [state.frameId]: nextList };
      });
    };
    const onPointerUp = () => {
      iconDragStateRef.current = null;
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, []);

  const closeFrameDetailDialog = () => {
    if (frameDetailUploadingBackground || frameDetailUploadingSamples || deletingSampleId) {
      return;
    }
    setFrameDetailOpen(false);
    setFrameDetail(null);
    setFrameDetailScope(null);
    setDetailFrameMeta(null);
  };

  const applyBackgroundFromLibrary = async (backgroundId: string) => {
    if (!backgroundId || !editing?._id || !detailFrameMeta?._id) return;
    try {
      setFrameDetailUploadingBackground(true);
      await api.applySharedFrameBackgroundToAlbumFrame(editing._id, detailFrameMeta._id, backgroundId);
      await refreshFrameDetail(editing._id, detailFrameMeta._id, { pruneSampleKeysToFrameIds: selectedFrameIds });
      toast.success('Đã áp dụng background cho frame');
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || 'Không thể áp dụng background');
    } finally {
      setFrameDetailUploadingBackground(false);
    }
  };

  const uploadBackgroundImage = async (file?: File) => {
    if (!file || !editing?._id || !detailFrameMeta?._id) return;
    try {
      setFrameDetailUploadingBackground(true);
      setUploadingSharedFrameBackground(true);
      const uploaded = await api.uploadSharedFrameBackground(file);
      await loadSharedBackgrounds();
      await api.applySharedFrameBackgroundToAlbumFrame(editing._id, detailFrameMeta._id, uploaded.background._id);
      await refreshFrameDetail(editing._id, detailFrameMeta._id, { pruneSampleKeysToFrameIds: selectedFrameIds });
      toast.success('Đã upload vào thư viện và áp dụng background');
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || 'Không thể upload background frame');
    } finally {
      setUploadingSharedFrameBackground(false);
      setFrameDetailUploadingBackground(false);
    }
  };

  const deleteBackgroundImage = async () => {
    if (!editing?._id || !detailFrameMeta?._id) return;
    try {
      setFrameDetailUploadingBackground(true);
      await api.deleteAlbumFrameBackgroundImage(editing._id, detailFrameMeta._id);
      await refreshFrameDetail(editing._id, detailFrameMeta._id, { pruneSampleKeysToFrameIds: selectedFrameIds });
      toast.success('Đã xóa background frame');
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || 'Không thể xóa background frame');
    } finally {
      setFrameDetailUploadingBackground(false);
    }
  };

  const applyIconFromLibrary = async (iconId: string) => {
    if (!iconId || !editing?._id || !detailFrameMeta?._id) return;
    try {
      setFrameDetailUploadingBackground(true);
      await api.applySharedFrameIconToAlbumFrame(editing._id, detailFrameMeta._id, iconId);
      await refreshFrameDetail(editing._id, detailFrameMeta._id, { pruneSampleKeysToFrameIds: selectedFrameIds });
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || 'Không thể áp dụng icon');
    } finally {
      setFrameDetailUploadingBackground(false);
    }
  };

  const uploadIconImage = async (file?: File) => {
    if (!file || !editing?._id || !detailFrameMeta?._id) return;
    if (file.size > 500 * 1024) {
      toast.error('Icon phải nhỏ hơn 500KB');
      return;
    }
    try {
      setFrameDetailUploadingBackground(true);
      setUploadingSharedFrameIcon(true);
      const uploaded = await api.uploadSharedFrameIcon(file);
      await loadSharedIcons();
      await api.applySharedFrameIconToAlbumFrame(editing._id, detailFrameMeta._id, uploaded.icon._id);
      await refreshFrameDetail(editing._id, detailFrameMeta._id, { pruneSampleKeysToFrameIds: selectedFrameIds });
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || 'Không thể upload icon');
    } finally {
      setUploadingSharedFrameIcon(false);
      setFrameDetailUploadingBackground(false);
    }
  };

  const deleteIconImage = async () => {
    if (!editing?._id || !detailFrameMeta?._id) return;
    try {
      setFrameDetailUploadingBackground(true);
      await api.deleteAlbumFrameIconImage(editing._id, detailFrameMeta._id);
      await refreshFrameDetail(editing._id, detailFrameMeta._id, { pruneSampleKeysToFrameIds: selectedFrameIds });
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || 'Không thể xóa icon frame');
    } finally {
      setFrameDetailUploadingBackground(false);
    }
  };

  const deleteSharedIconItem = async (item: api.SharedFrameIcon) => {
    if (!item?._id) return;
    const accepted = window.confirm('Xóa icon này khỏi thư viện?');
    if (!accepted) return;
    try {
      setDeletingSharedIconId(item._id);
      const result = await api.deleteSharedFrameIcon(item._id);
      const targetUrl = String(item.url || '').trim();
      await loadSharedIcons();
      if (targetUrl) {
        setPreviewIconsByFrameId((prev) =>
          Object.entries(prev).reduce<Record<string, PreviewPageIcon[]>>((acc, [frameId, icons]) => {
            const filtered = (icons || []).filter((entry) => String(entry.url || '').trim() !== targetUrl);
            if (filtered.length > 0) acc[frameId] = filtered;
            return acc;
          }, {})
        );
        setFrameIconByFrameId((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((frameId) => {
            if (String(next[frameId] || '').trim() === targetUrl) delete next[frameId];
          });
          return next;
        });
        setForm((prev) =>
          String(prev.coverIconImageUrl || '').trim() === targetUrl
            ? { ...prev, coverIconImageUrl: '' }
            : prev
        );
      }
      if (editing?._id && detailFrameMeta?._id) {
        await refreshFrameDetail(editing._id, detailFrameMeta._id, { pruneSampleKeysToFrameIds: selectedFrameIds });
      }
      if ((result.detachedCount || 0) > 0) {
        toast.success(`Đã xóa icon khỏi thư viện và gỡ khỏi ${result.detachedCount} frame`);
      } else {
        toast.success('Đã xóa icon khỏi thư viện');
      }
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || 'Không thể xóa icon khỏi thư viện');
    } finally {
      setDeletingSharedIconId(null);
    }
  };

  const addPreviewIconToSelectedPage = (iconUrl: string) => {
    const frameId = String(selectedPageFrameId || '').trim();
    if (!frameId || !iconUrl.trim()) return;
    const nextItem: PreviewPageIcon = {
      id: `pi_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      url: iconUrl.trim(),
      xPct: 50,
      yPct: 16,
      sizePct: 18,
      rotateDeg: 0,
    };
    setPreviewIconsByFrameId((prev) => ({
      ...prev,
      [frameId]: [...(prev[frameId] || []), nextItem],
    }));
    setActivePreviewIcon({ frameId, iconId: nextItem.id });
  };

  const removePreviewIconFromSelectedPage = (iconId: string) => {
    const frameId = String(selectedPageFrameId || '').trim();
    if (!frameId || !iconId) return;
    setPreviewIconsByFrameId((prev) => ({
      ...prev,
      [frameId]: (prev[frameId] || []).filter((item) => item.id !== iconId),
    }));
    setActivePreviewIcon((prev) => (prev && prev.frameId === frameId && prev.iconId === iconId ? null : prev));
  };

  const scrollBackgroundCarousel = (direction: 'left' | 'right') => {
    const node = backgroundCarouselRef.current;
    if (!node) return;
    const delta = Math.max(220, Math.round(node.clientWidth * 0.7));
    node.scrollBy({
      left: direction === 'left' ? -delta : delta,
      behavior: 'smooth',
    });
  };

  const deleteSharedBackground = async (item: api.SharedFrameBackground) => {
    setSharedBackgroundToDelete(item);
  };

  const confirmDeleteSharedBackground = async () => {
    const item = sharedBackgroundToDelete;
    if (!item?._id) return;
    try {
      setConfirmDeleting(true);
      setDeletingSharedBackgroundId(item._id);
      const result = await api.deleteSharedFrameBackground(item._id);
      await loadSharedBackgrounds();
      if (editing?._id && detailFrameMeta?._id) {
        await refreshFrameDetail(editing._id, detailFrameMeta._id, { pruneSampleKeysToFrameIds: selectedFrameIds });
      }
      if ((result.detachedCount || 0) > 0) {
        toast.success(`Đã xóa background khỏi thư viện và gỡ khỏi ${result.detachedCount} frame`);
      } else {
        toast.success('Đã xóa background khỏi thư viện');
      }
      setSharedBackgroundToDelete(null);
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || 'Không thể xóa background khỏi thư viện');
    } finally {
      setDeletingSharedBackgroundId(null);
      setConfirmDeleting(false);
    }
  };

  const uploadSampleImages = async (files?: FileList | null) => {
    const frameId = String(selectedPageFrame?._id || detailFrameMeta?._id || '').trim();
    if (!files || files.length === 0 || !editing?._id || !frameId) return;
    if (files.length > 10) {
      toast.error('Mỗi lần chỉ upload tối đa 10 khung');
      return;
    }
    try {
      setFrameDetailUploadingSamples(true);
      await api.uploadAlbumFrameSampleImages(editing._id, frameId, Array.from(files));
      await refreshFrameDetail(editing._id, frameId, { pruneSampleKeysToFrameIds: selectedFrameIds });
      toast.success('Đã thêm khung');
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || 'Không thể thêm khung');
    } finally {
      setFrameDetailUploadingSamples(false);
    }
  };

  const deleteSampleImage = async (sampleImageId: string) => {
    const frameId = String(selectedPageFrame?._id || detailFrameMeta?._id || '').trim();
    if (!editing?._id || !frameId) return;
    try {
      setDeletingSampleId(sampleImageId);
      await api.deleteAlbumFrameSampleImage(editing._id, frameId, sampleImageId);
      await refreshFrameDetail(editing._id, frameId, { pruneSampleKeysToFrameIds: selectedFrameIds });
      toast.success('Đã xóa khung');
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || 'Không thể xóa khung');
    } finally {
      setDeletingSampleId(null);
    }
  };

  const togglePageFrameVisibility = async (frameId: string, enabled: boolean) => {
    const albumId = editing?._id;
    if (!albumId || togglingFrameId || reorderingFrameId) return;
    try {
      setTogglingFrameId(frameId);
      const result = await api.toggleAlbumFrameVisibility(albumId, frameId, enabled);
      const nextIds = Array.isArray(result.enabledFrameIds)
        ? result.enabledFrameIds.map(String)
        : enabled
          ? Array.from(new Set([...selectedFrameIds, frameId]))
          : selectedFrameIds.filter((id) => id !== frameId);
      setSelectedFrameIds(nextIds);
      setOrderDraftByFrameId({});
      setEditing((album) => (album ? { ...album, enabledFrameIds: nextIds } : album));
      setAlbums((list) =>
        list.map((x) => (x._id === albumId ? { ...x, enabledFrameIds: nextIds } : x))
      );
      if (enabled) {
        setSelectedPageFrameId(frameId);
      } else if (selectedPageFrameId === frameId) {
        setSelectedPageFrameId(nextIds[0] || frameId);
      }
      toast.success(enabled ? 'Đã bật frame' : 'Đã tắt frame');
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string; message?: string } } }).response?.data
              ?.error ||
            (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || 'Không thể cập nhật frame');
    } finally {
      setTogglingFrameId(null);
    }
  };

  const commitPageFrameOrder = async (frameId: string, rawOrder: string) => {
    const albumId = editing?._id;
    if (!albumId || togglingFrameId || reorderingFrameId) return;

    const fromIdx = selectedFrameIds.indexOf(frameId);
    if (fromIdx < 0) {
      setOrderDraftByFrameId((prev) => {
        const next = { ...prev };
        delete next[frameId];
        return next;
      });
      return;
    }

    const parsed = Number.parseInt(String(rawOrder || '').trim(), 10);
    if (!Number.isFinite(parsed)) {
      setOrderDraftByFrameId((prev) => {
        const next = { ...prev };
        delete next[frameId];
        return next;
      });
      return;
    }

    const toIdx = Math.max(0, Math.min(selectedFrameIds.length - 1, parsed - 1));
    if (toIdx === fromIdx) {
      setOrderDraftByFrameId((prev) => {
        const next = { ...prev };
        delete next[frameId];
        return next;
      });
      return;
    }

    const nextIds = [...selectedFrameIds];
    nextIds.splice(fromIdx, 1);
    nextIds.splice(toIdx, 0, frameId);

    try {
      setReorderingFrameId(frameId);
      await api.updatePublicMenuAlbum(albumId, { enabledFrameIds: nextIds });
      setSelectedFrameIds(nextIds);
      setOrderDraftByFrameId({});
      setEditing((album) => (album ? { ...album, enabledFrameIds: nextIds } : album));
      setAlbums((list) =>
        list.map((x) => (x._id === albumId ? { ...x, enabledFrameIds: nextIds } : x))
      );
      toast.success(`Đã đặt thứ tự trang thành ${toIdx + 1}`);
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string; message?: string } } }).response?.data
              ?.error ||
            (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || 'Không thể cập nhật thứ tự trang');
      setOrderDraftByFrameId((prev) => {
        const next = { ...prev };
        delete next[frameId];
        return next;
      });
    } finally {
      setReorderingFrameId(null);
    }
  };

  const pinkSwitchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: PINK },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PINK },
  };

  return (
    <UserPageShell enter>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          mb: { xs: 1.5, sm: 2.5 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h1"
            fontWeight={800}
            sx={{
              fontSize: { xs: '1.25rem', sm: '1.85rem' },
              letterSpacing: '-0.03em',
              lineHeight: 1.2,
              color: '#2d2430',
            }}
          >
            {a.albums.title}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mt: 0.4, fontSize: { xs: '0.78rem', sm: '0.9rem' }, fontWeight: 600 }}
          >
            {a.albums.subtitle}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={!isMobile ? <Add /> : undefined}
          onClick={() => void createEmptyAlbumImmediately()}
          disabled={loading || creatingAlbum}
          sx={{
            flexShrink: 0,
            borderRadius: 999,
            textTransform: 'none',
            fontWeight: 800,
            boxShadow: 'none',
            bgcolor: PINK,
            '&:hover': { bgcolor: alpha(PINK, 0.88) },
            ...(isMobile ? { minWidth: 40, width: 40, height: 40, p: 0 } : { px: 2 }),
          }}
          aria-label={a.albums.createNew}
        >
          {isMobile ? (
            creatingAlbum ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <Add />
          ) : creatingAlbum ? (
            a.albums.creating
          ) : (
            a.albums.createNew
          )}
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress sx={{ color: PINK }} />
        </Box>
      ) : albums.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed',
            borderColor: alpha(PINK_SOFT, 0.5),
            background: `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha(PINK_SOFT, 0.08)} 100%)`,
          }}
        >
          <ImageOutlined sx={{ fontSize: 52, color: alpha(PINK, 0.45), mb: 1.5 }} />
          <Typography
            color="text.secondary"
            fontWeight={700}
            sx={{ mb: 2, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}
          >
            {a.albums.empty}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => void createEmptyAlbumImmediately()}
            disabled={creatingAlbum}
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 800,
              boxShadow: 'none',
              bgcolor: PINK,
              '&:hover': { bgcolor: alpha(PINK, 0.88) },
            }}
          >
            {creatingAlbum ? a.albums.creating : a.albums.createNew}
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={{ xs: 1.5, sm: 2.5 }}>
          {albums.map((album, index) => {
            const albumLink = `${PUBLIC_APP_ORIGIN}/public/custom-frame-menu/${album.slug}`;
            return (
              <Grid item xs={12} md={6} key={album._id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: album.isActive ? alpha(PINK, 0.42) : alpha(PINK_SOFT, 0.28),
                    boxShadow: album.isActive
                      ? `0 8px 22px ${alpha(PINK, 0.12)}`
                      : `0 8px 22px ${alpha('#bf5f82', 0.07)}`,
                    background: `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha(PINK_SOFT, 0.08)} 100%)`,
                    ...userCardEnterSx(index),
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        fontWeight={800}
                        noWrap
                        title={album.name}
                        sx={{
                          color: '#2d2430',
                          fontSize: { xs: '0.98rem', sm: '1.12rem' },
                          lineHeight: 1.3,
                        }}
                      >
                        {album.name}
                      </Typography>
                      <Typography
                        sx={{
                          color: alpha(PINK, 0.72),
                          fontWeight: 600,
                          fontSize: { xs: '0.72rem', sm: '0.8rem' },
                          mt: 0.25,
                        }}
                      >
                        {a.albums.pathSuffix}: {album.slug}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" flexShrink={0}>
                      <Box display="flex" alignItems="center" gap={0.25}>
                        <Tooltip title={album.isActive ? a.albums.enabled : a.albums.disabled}>
                          <Switch
                            checked={Boolean(album.isActive)}
                            onChange={(e) => toggleActive(album, e.target.checked)}
                            size="small"
                            sx={pinkSwitchSx}
                          />
                        </Tooltip>
                        <Tooltip title={t.common.edit}>
                          <IconButton
                            onClick={() => openEdit(album)}
                            size="small"
                            sx={{
                              color: PINK,
                              bgcolor: alpha(PINK, 0.1),
                              '&:hover': { bgcolor: alpha(PINK, 0.2) },
                            }}
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box sx={{ ml: 1, pl: 1, borderLeft: `1px solid ${alpha(PINK_SOFT, 0.4)}` }}>
                        <Tooltip title={t.common.delete}>
                          <IconButton
                            onClick={() => removeAlbum(album)}
                            size="small"
                            sx={{
                              color: '#e53935',
                              bgcolor: alpha('#e53935', 0.08),
                              '&:hover': { bgcolor: alpha('#e53935', 0.16) },
                            }}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 1.5 }}>
                    <Typography
                      component="label"
                      sx={{
                        display: 'block',
                        mb: 0.7,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        color: alpha(PINK, 0.78),
                      }}
                    >
                      {a.albums.albumLink}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box
                        component="a"
                        href={albumLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={albumLink}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          display: 'block',
                          px: 1.25,
                          py: 1,
                          borderRadius: 2.5,
                          bgcolor: '#fff',
                          border: '1px solid',
                          borderColor: alpha(PINK_SOFT, 0.42),
                          boxShadow: `0 1px 3px ${alpha(PINK, 0.06)}`,
                          color: '#5a4450',
                          fontSize: { xs: '0.75rem', sm: '0.82rem' },
                          fontWeight: 600,
                          textDecoration: 'none',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          '&:hover': {
                            borderColor: PINK,
                            color: PINK,
                          },
                        }}
                      >
                        {albumLink}
                      </Box>
                      <IconButton
                        onClick={() => copyText('link album', albumLink)}
                        size="small"
                        sx={{
                          flexShrink: 0,
                          color: PINK,
                          bgcolor: '#fff',
                          border: '1px solid',
                          borderColor: alpha(PINK_SOFT, 0.42),
                          borderRadius: 2,
                          width: 40,
                          height: 40,
                          '&:hover': { bgcolor: alpha(PINK, 0.1), borderColor: PINK },
                        }}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 1.5, flex: 1 }}>
                    <Typography
                      component="label"
                      sx={{
                        display: 'block',
                        mb: 0.7,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        color: alpha(PINK, 0.78),
                      }}
                    >
                      {a.albums.coverImage}
                    </Typography>
                    {album.coverImageUrl ? (
                      <Box
                        component="img"
                        src={album.coverImageUrl}
                        alt={`cover-${album.slug}`}
                        sx={{
                          width: '100%',
                          height: { xs: 180, sm: 220, md: 240 },
                          objectFit: 'contain',
                          backgroundColor: alpha(PINK_SOFT, 0.08),
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: alpha(PINK_SOFT, 0.28),
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: { xs: 120, sm: 148 },
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.75,
                          borderRadius: 2,
                          border: '1px dashed',
                          borderColor: alpha(PINK_SOFT, 0.5),
                          bgcolor: alpha(PINK_SOFT, 0.06),
                        }}
                      >
                        <ImageOutlined sx={{ fontSize: 32, color: alpha(PINK, 0.4) }} />
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: alpha('#2d2430', 0.5),
                          }}
                        >
                          {a.albums.noCover}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog
        open={open}
        onClose={closeDialog}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            width: '95vw',
            maxWidth: '1600px',
            height: { xs: '96vh', md: '96vh' },
          },
        }}
      >
        <DialogTitle>{editing ? a.albums.editDialog : a.albums.createDialog}</DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden', overflowY: 'auto' }}>
          <Grid
            container
            spacing={2}
            sx={{
              mt: 0.5,
              height: { xs: 'auto', md: '100%' },
              pb: { xs: 1, md: 0 },
            }}
          >
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                maxHeight: { xs: 'none', md: '100%' },
                overflowY: {
                  xs: 'visible',
                  md: 'auto',
                },
                overflowX: 'hidden',
                pr: { md: 1 },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: { xs: 1, md: 2 } }}>
                <TextField
                  fullWidth
                  label={a.albums.pageName}
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  sx={albumFieldSx}
                />
                <TextField
                  fullWidth
                  label={a.albums.pathSuffix}
                  value={form.slug}
                  onChange={(e) => setForm((prev) => ({ ...prev, slug: toSlug(e.target.value) }))}
                  sx={albumFieldSx}
                />
              </Box>

              <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {a.albums.isActive}
                </Typography>
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                  sx={pinkSwitchSx}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {a.albums.pageList}
                </Typography>
                {orderedFrameSizes.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.75,
                      flexWrap: 'wrap',
                      mb: 1.25,
                    }}
                  >
                    <Button
                      size="small"
                      variant={selectedFrameSizes.length === 0 ? 'contained' : 'outlined'}
                      onClick={() => setSelectedFrameSizes([])}
                      sx={{ textTransform: 'none', minWidth: 0, px: 1.25 }}
                    >
                      Tất cả
                    </Button>
                    {orderedFrameSizes.map((size) => (
                      <Button
                        key={`page-size-filter-${size}`}
                        size="small"
                        variant={selectedFrameSizes.includes(size) ? 'contained' : 'outlined'}
                        onClick={() => toggleFrameSizeFilter(size)}
                        sx={{ textTransform: 'none', minWidth: 0, px: 1.25 }}
                      >
                        {size}
                      </Button>
                    ))}
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'row', md: 'column' },
                    gap: 1,
                    pr: { xs: 0, md: 0.1 },
                    pb: { xs: 0.5, md: 0 },
                    overflowX: { xs: 'auto', md: 'visible' },
                    overflowY: { xs: 'hidden', md: 'visible' },
                    scrollSnapType: { xs: 'x mandatory', md: 'none' },
                    '&::-webkit-scrollbar': { height: 8 },
                    '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: 8 },
                  }}
                >
                  {pageListFrames.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      Không có frame trong bộ lọc hiện tại.
                    </Typography>
                  ) : null}

                  {pageListFrames.map((frame) => {
                    const enabled = selectedFrameIds.includes(frame._id);
                    const enabledIdx = enabled
                      ? enabledFrames.findIndex((f) => f._id === frame._id)
                      : -1;
                    const pageNo =
                      enabledIdx >= 0 ? String(enabledIdx + 1).padStart(2, '0') : '';
                    const thumbUrl = String(
                      mainImageByFrameId[frame._id] || frame.frameImage?.url || ''
                    ).trim();
                    const isSelectedPage = selectedPageFrameId === frame._id;
                    return (
                      <Paper
                        key={`page-frame-${frame._id}`}
                        variant="outlined"
                        onClick={() => {
                          setSelectedPageFrameId(frame._id);
                        }}
                        sx={{
                          p: 1,
                          borderRadius: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1.25,
                          minWidth: { xs: 250, md: 0 },
                          width: { xs: 250, md: '100%' },
                          flexShrink: 0,
                          scrollSnapAlign: { xs: 'start', md: 'none' },
                          cursor: 'pointer',
                          opacity: enabled ? 1 : 0.72,
                          borderColor: isSelectedPage ? '#60a5fa' : '#e5e7eb',
                          backgroundColor: isSelectedPage ? '#eff6ff' : '#fff',
                        }}
                      >
                        <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 44,
                              height: 62,
                              borderRadius: 0.75,
                              border: 'none',
                              overflow: 'visible',
                              backgroundColor: '#f8fafc',
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 0.35,
                            }}
                          >
                            {thumbUrl ? (
                              <Box
                                component="img"
                                src={thumbUrl}
                                alt={pageNo ? `frame-page-${pageNo}` : frame.name}
                                sx={{
                                  width: 'auto',
                                  height: 'auto',
                                  maxWidth: '92%',
                                  maxHeight: '92%',
                                  objectFit: 'contain',
                                  backgroundColor: 'transparent',
                                  border: 'none',
                                  borderRadius: 0,
                                  filter:
                                    'drop-shadow(1px 0 0 #dbe4f0) drop-shadow(-1px 0 0 #dbe4f0) ' +
                                    'drop-shadow(0 1px 0 #dbe4f0) drop-shadow(0 -1px 0 #dbe4f0)',
                                }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                {pageNo || '—'}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                              {enabled && pageNo
                                ? formatMessage(a.albums.framePage, {
                                    n: pageNo,
                                    name: frame.name || 'Custom frame',
                                  })
                                : frame.name || 'Custom frame'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {`${frame.size || 'khác'} · ${frame.maxPhotos || 0} ô ảnh`}
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            minWidth: enabled ? 108 : 56,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 0.75,
                          }}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {enabled ? (
                            <TextField
                              size="small"
                              type="number"
                              label={a.albums.pageOrder}
                              value={
                                orderDraftByFrameId[frame._id] !== undefined
                                  ? orderDraftByFrameId[frame._id]
                                  : String(enabledIdx + 1)
                              }
                              disabled={Boolean(togglingFrameId || reorderingFrameId)}
                              onFocus={() => {
                                setOrderDraftByFrameId((prev) => ({
                                  ...prev,
                                  [frame._id]: String(enabledIdx + 1),
                                }));
                              }}
                              onChange={(e) => {
                                setOrderDraftByFrameId((prev) => ({
                                  ...prev,
                                  [frame._id]: e.target.value,
                                }));
                              }}
                              onBlur={(e) => {
                                void commitPageFrameOrder(frame._id, e.target.value);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                              inputProps={{
                                min: 1,
                                max: Math.max(1, selectedFrameIds.length),
                                step: 1,
                                style: { textAlign: 'center', padding: '6px 4px' },
                              }}
                              sx={{
                                width: 52,
                                '& .MuiInputLabel-root': { fontSize: 11 },
                                '& .MuiInputBase-root': { fontSize: 13 },
                              }}
                            />
                          ) : null}
                          <Switch
                            size="small"
                            checked={enabled}
                            disabled={Boolean(togglingFrameId || reorderingFrameId)}
                            onChange={(e) => {
                              void togglePageFrameVisibility(frame._id, e.target.checked);
                            }}
                          />
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: { xs: 520, md: 620 },
              }}
            >
              <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Typography variant="subtitle2">{a.albums.demoPreview}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant={centerPreviewMode === 'page' ? 'contained' : 'outlined'}
                      onClick={() => setCenterPreviewMode('page')}
                      disabled={saving}
                      sx={{ textTransform: 'none' }}
                    >
                      Trang đang chọn
                    </Button>
                    <Button
                      size="small"
                      variant={centerPreviewMode === 'album' ? 'contained' : 'outlined'}
                      onClick={() => setCenterPreviewMode('album')}
                      disabled={saving}
                      sx={{ textTransform: 'none' }}
                    >
                      Live view album
                    </Button>
                  </Box>
                  {centerPreviewMode === 'album' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => updatePreviewZoom(-PREVIEW_ZOOM_STEP)}
                        disabled={previewZoom <= MIN_PREVIEW_ZOOM || saving}
                      >
                        -
                      </Button>
                      <Button size="small" variant="text" onClick={resetPreviewZoom} disabled={saving}>
                        {Math.round(previewZoom * 100)}%
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => updatePreviewZoom(PREVIEW_ZOOM_STEP)}
                        disabled={previewZoom >= MAX_PREVIEW_ZOOM || saving}
                      >
                        +
                      </Button>
                    </Box>
                  ) : null}
                </Box>
              </Box>
              <Paper
                variant="outlined"
                sx={{ flex: 1, minHeight: { xs: 500, md: 620 }, overflow: 'hidden' }}
              >
                {centerPreviewMode === 'album' ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <iframe
                      ref={previewIframeRef}
                      title="album-live-preview"
                      src={previewUrl}
                      onLoad={pushPreviewToIframe}
                      scrolling="no"
                      style={{ width: '90%', height: '90%', border: 0 }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                    {selectedPageFrame ? (
                      <Box
                        sx={{
                          width: { xs: 'min(90vw, 320px)', md: 360 },
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                        }}
                      >
                        <Box
                          ref={selectedPagePreviewRef}
                          onPointerDownCapture={(event) => {
                            const target = event.target as HTMLElement | null;
                            if (target?.closest('[data-preview-icon-root="1"]')) return;
                            setActivePreviewIcon(null);
                          }}
                          sx={{
                            width: '100%',
                            aspectRatio: '1 / 1.45',
                            borderRadius: 1,
                            backgroundColor: '#fff',
                            overflow: 'hidden',
                            position: 'relative',
                            boxShadow: '0 16px 34px rgba(0,0,0,0.18)',
                            p: 1.1,
                            cursor: 'default',
                          }}
                        >
                        {String(frameDetail?.config?.backgroundImage?.url || '').trim() ? (
                          <Box
                            component="img"
                            src={String(frameDetail?.config?.backgroundImage?.url || '').trim()}
                            alt="selected-page-bg"
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              pointerEvents: 'none',
                            }}
                          />
                        ) : null}
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            background: String(frameDetail?.config?.backgroundImage?.url || '').trim()
                              ? 'rgba(255,255,255,0.35)'
                              : 'transparent',
                            pointerEvents: 'none',
                          }}
                        />
                        {(() => {
                          const frameId = String(selectedPageFrame?._id || '').trim();
                          const previewIcons = previewIconsByFrameId[frameId] || [];
                          return previewIcons.map((icon) => (
                            <Box
                              key={icon.id}
                              data-preview-icon-root="1"
                              sx={{
                                position: 'absolute',
                                left: `${icon.xPct}%`,
                                top: `${icon.yPct}%`,
                                width: `${icon.sizePct}%`,
                                aspectRatio: '1 / 1',
                                transform: `translate(-50%, -50%) rotate(${icon.rotateDeg || 0}deg)`,
                                zIndex: 2,
                                cursor: 'grab',
                              }}
                              onPointerDown={(event) => {
                                event.stopPropagation();
                                event.preventDefault();
                                const node = selectedPagePreviewRef.current;
                                const rect = node?.getBoundingClientRect();
                                const centerX = rect ? rect.left + (icon.xPct / 100) * rect.width : event.clientX;
                                const centerY = rect ? rect.top + (icon.yPct / 100) * rect.height : event.clientY;
                                const pointerAngleDeg =
                                  (Math.atan2(event.clientY - centerY, event.clientX - centerX) * 180) / Math.PI;
                                setActivePreviewIcon({ frameId, iconId: icon.id });
                                iconDragStateRef.current = {
                                  frameId,
                                  iconId: icon.id,
                                  mode: 'move',
                                  startX: event.clientX,
                                  startY: event.clientY,
                                  startXPct: icon.xPct,
                                  startYPct: icon.yPct,
                                  startSizePct: icon.sizePct,
                                  startRotateDeg: icon.rotateDeg || 0,
                                  startPointerAngleDeg: pointerAngleDeg,
                                };
                              }}
                              onClick={(event) => {
                                event.stopPropagation();
                              }}
                            >
                              <Box
                                component="img"
                                src={icon.url}
                                alt="preview-icon"
                                sx={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
                              />
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  removePreviewIconFromSelectedPage(icon.id);
                                }}
                                sx={{
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  bgcolor: 'rgba(255,255,255,0.92)',
                                  border: '1px solid rgba(248,113,113,0.4)',
                                  width: 20,
                                  height: 20,
                                  display:
                                    activePreviewIcon?.frameId === frameId && activePreviewIcon?.iconId === icon.id
                                      ? 'inline-flex'
                                      : 'none',
                                }}
                              >
                                <DeleteOutline sx={{ fontSize: 12 }} />
                              </IconButton>
                              {(['tl', 'br'] as PreviewIconHandle[]).map((handle) => (
                                <Box
                                  key={`${icon.id}-${handle}`}
                                  onPointerDown={(event) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    const node = selectedPagePreviewRef.current;
                                    const rect = node?.getBoundingClientRect();
                                    const centerX = rect ? rect.left + (icon.xPct / 100) * rect.width : event.clientX;
                                    const centerY = rect ? rect.top + (icon.yPct / 100) * rect.height : event.clientY;
                                    const pointerAngleDeg =
                                      (Math.atan2(event.clientY - centerY, event.clientX - centerX) * 180) / Math.PI;
                                    setActivePreviewIcon({ frameId, iconId: icon.id });
                                    iconDragStateRef.current = {
                                      frameId,
                                      iconId: icon.id,
                                      mode: 'resize',
                                      handle,
                                      startX: event.clientX,
                                      startY: event.clientY,
                                      startXPct: icon.xPct,
                                      startYPct: icon.yPct,
                                      startSizePct: icon.sizePct,
                                      startRotateDeg: icon.rotateDeg || 0,
                                      startPointerAngleDeg: pointerAngleDeg,
                                    };
                                  }}
                                  sx={{
                                    position: 'absolute',
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: '#fff',
                                    border: '1px solid #ec4899',
                                    display:
                                      activePreviewIcon?.frameId === frameId && activePreviewIcon?.iconId === icon.id
                                        ? 'block'
                                        : 'none',
                                    ...(handle === 'tl' ? { top: -5, left: -5, cursor: 'nwse-resize' } : {}),
                                    ...(handle === 'br' ? { bottom: -5, right: -5, cursor: 'nwse-resize' } : {}),
                                  }}
                                />
                              ))}
                              <Box
                                onPointerDown={(event) => {
                                  event.stopPropagation();
                                  event.preventDefault();
                                  const node = selectedPagePreviewRef.current;
                                  const rect = node?.getBoundingClientRect();
                                  const centerX = rect ? rect.left + (icon.xPct / 100) * rect.width : event.clientX;
                                  const centerY = rect ? rect.top + (icon.yPct / 100) * rect.height : event.clientY;
                                  const pointerAngleDeg =
                                    (Math.atan2(event.clientY - centerY, event.clientX - centerX) * 180) / Math.PI;
                                  setActivePreviewIcon({ frameId, iconId: icon.id });
                                  iconDragStateRef.current = {
                                    frameId,
                                    iconId: icon.id,
                                    mode: 'rotate',
                                    startX: event.clientX,
                                    startY: event.clientY,
                                    startXPct: icon.xPct,
                                    startYPct: icon.yPct,
                                    startSizePct: icon.sizePct,
                                    startRotateDeg: icon.rotateDeg || 0,
                                    startPointerAngleDeg: pointerAngleDeg,
                                  };
                                }}
                                sx={{
                                  position: 'absolute',
                                  bottom: -24,
                                  right: -24,
                                  width: 18,
                                  height: 18,
                                  borderRadius: '50%',
                                  backgroundColor: '#fff',
                                  border: '1px solid #22c55e',
                                  color: '#16a34a',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'alias',
                                  display:
                                    activePreviewIcon?.frameId === frameId && activePreviewIcon?.iconId === icon.id
                                      ? 'flex'
                                      : 'none',
                                }}
                              >
                                <RotateRight sx={{ fontSize: 12 }} />
                              </Box>
                            </Box>
                          ));
                        })()}
                        <Box
                          component="img"
                          src={(() => {
                            const fid = String(selectedPageFrame._id || '').trim();
                            return String(
                              mainImageByFrameId[fid] ||
                                frameDetail?.config?.mainImage?.url ||
                                selectedPageFrame.frameImage?.url ||
                                ''
                            ).trim();
                          })()}
                          alt="selected-page-main"
                          sx={{
                            position: 'absolute',
                            left: '50%',
                            top: '40%',
                            width: '90%',
                            maxHeight: '72%',
                            objectFit: 'contain',
                            zIndex: 1,
                            transform: 'translate(-50%, -50%)',
                            pointerEvents: 'none',
                            filter:
                              'drop-shadow(1px 0 0 #cbd5e1) drop-shadow(-1px 0 0 #cbd5e1) ' +
                              'drop-shadow(0 1px 0 #cbd5e1) drop-shadow(0 -1px 0 #cbd5e1)',
                          }}
                        />
                        </Box>
                      </Box>
                    ) : null}
                  </Box>
                )}
              </Paper>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                {centerPreviewMode === 'album'
                  ? 'Zoom demo: dùng nút +/- hoặc Ctrl + / Ctrl - / Ctrl 0.'
                  : 'Chế độ Trang đang chọn: dùng cột bên phải để thêm / xóa khung cho frame.'}
              </Typography>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                maxHeight: { xs: 'none', md: '100%' },
                overflowY: { xs: 'visible', md: 'auto' },
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Thêm khung cho frame
              </Typography>
              {!selectedPageFrame ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Chọn một frame ở danh sách bên trái để thêm / xóa khung.
                  </Typography>
                </Paper>
              ) : !editing?._id ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Lưu album trước khi thêm khung cho frame.
                  </Typography>
                </Paper>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 1.25,
                    borderColor: '#f3c7d8',
                    backgroundColor: '#fff9fc',
                    flex: 1,
                    minHeight: { xs: 280, md: 0 },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }} noWrap>
                    {selectedPageFrame.name || 'Custom frame'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25 }}>
                    {`${selectedPageFrame.size || 'khác'} · ${selectedPageFrame.maxPhotos || 0} ô ảnh`}
                    {' · '}
                    Upload Firebase/CDN, xóa sẽ gỡ luôn trên storage.
                  </Typography>

                  <Box
                    component="label"
                    sx={{
                      ...uploadDropSx,
                      minHeight: 96,
                      py: 1.5,
                      cursor:
                        frameDetailLoading || frameDetailUploadingSamples || Boolean(deletingSampleId)
                          ? 'not-allowed'
                          : 'pointer',
                      opacity:
                        frameDetailLoading || frameDetailUploadingSamples || Boolean(deletingSampleId) ? 0.6 : 1,
                    }}
                  >
                    {frameDetailUploadingSamples ? (
                      <CircularProgress size={24} />
                    ) : (
                      <CloudUploadRounded sx={{ color: '#eba3bf', fontSize: 28 }} />
                    )}
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>
                      {frameDetailUploadingSamples ? 'Đang upload...' : 'Thêm khung (kéo thả hoặc chọn ảnh)'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tối đa 10 ảnh mỗi lần
                    </Typography>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      hidden
                      disabled={
                        frameDetailLoading || frameDetailUploadingSamples || Boolean(deletingSampleId)
                      }
                      onChange={(e) => {
                        void uploadSampleImages(e.target.files);
                        e.currentTarget.value = '';
                      }}
                    />
                  </Box>

                  {frameDetailLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : (
                    <Grid container spacing={1} sx={{ mt: 1 }}>
                      {scopedSampleImagesForSelectedPage.map((sample) => (
                        <Grid item xs={6} key={sample._id || sample.url}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 0.75,
                              borderRadius: 0,
                              position: 'relative',
                              backgroundColor: '#fff',
                            }}
                          >
                            <Box
                              component="img"
                              src={sample.url}
                              alt={sample.name || 'khung'}
                              sx={{
                                width: '100%',
                                height: 110,
                                objectFit: 'contain',
                                backgroundColor: '#fafafa',
                                borderRadius: 0,
                                display: 'block',
                              }}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!sample._id || deletingSampleId === sample._id}
                              onClick={() => sample._id && void deleteSampleImage(sample._id)}
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: 'rgba(255,255,255,0.92)',
                                border: '1px solid rgba(248,113,113,0.35)',
                                width: 28,
                                height: 28,
                              }}
                            >
                              {deletingSampleId === sample._id ? (
                                <CircularProgress size={14} />
                              ) : (
                                <DeleteOutline sx={{ fontSize: 16 }} />
                              )}
                            </IconButton>
                          </Paper>
                        </Grid>
                      ))}
                      {scopedSampleImagesForSelectedPage.length === 0 && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Chưa có khung nào cho frame này.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Paper>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            {t.common.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={saving || uploadingCover || uploadingBackground}
          >
            {editing ? a.albums.saveChanges : a.albums.create}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={frameDetailOpen}
        onClose={closeFrameDetailDialog}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            width: '92vw',
            maxWidth: '1200px',
            height: '88vh',
          },
        }}
      >
        <DialogTitle>
          Chi tiết frame theo album
          {detailFrameMeta ? ` - ${detailFrameMeta.name}` : ''}
        </DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto' }}>
          {frameDetailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  borderColor: '#f3c7d8',
                  backgroundColor: '#fff9fc',
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Background trang theo frame: upload vào thư viện dùng chung, hoặc chọn từ thư viện để áp cho frame.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Box
                        component="label"
                        sx={{
                          ...uploadDropSx,
                          cursor:
                            frameDetailUploadingBackground || frameDetailUploadingSamples
                              ? 'not-allowed'
                              : 'pointer',
                          opacity:
                            frameDetailUploadingBackground || frameDetailUploadingSamples ? 0.6 : 1,
                        }}
                      >
                        {frameDetailUploadingBackground || uploadingSharedFrameBackground ? (
                          <CircularProgress size={26} />
                        ) : (
                          <CloudUploadRounded sx={{ color: '#eba3bf', fontSize: 32 }} />
                        )}
                        <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#1f2937' }}>
                          {frameDetailUploadingBackground || uploadingSharedFrameBackground
                            ? 'Đang upload...'
                            : 'Upload background mới vào thư viện'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Sau khi upload sẽ tự apply cho frame hiện tại
                        </Typography>
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          disabled={
                            frameDetailUploadingBackground || frameDetailUploadingSamples
                          }
                          onChange={(e) => {
                            openFrameBackgroundCropper(e.target.files?.[0]);
                            e.currentTarget.value = '';
                          }}
                        />
                      </Box>
                      <Button
                        variant="text"
                        color="error"
                        size="small"
                        onClick={deleteBackgroundImage}
                        disabled={
                          frameDetailUploadingBackground || !frameDetail?.config?.backgroundImage?.url
                        }
                      >
                        Xóa background trang
                      </Button>
                    </Box>
                  </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Demo ghép trang (background + khung chính)
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      width: '100%',
                      minHeight: 360,
                      borderRadius: 1.5,
                      p: 1.5,
                      backgroundColor: '#fff',
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        minHeight: { xs: 300, md: 360 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {(() => {
                        const previewFrameId = String(detailFrameMeta?._id || frameDetail?.frame?._id || '').trim();
                        const previewMainImageUrl = String(
                          (previewFrameId && mainImageByFrameId[previewFrameId]) ||
                            frameDetail?.config?.mainImage?.url ||
                            frameDetail?.frame?.frameImage?.url ||
                            ''
                        ).trim();
                        const previewBackgroundUrl = String(frameDetail?.config?.backgroundImage?.url || '').trim();
                        const previewSize = String(frameDetail?.frame?.size || '').trim().toLowerCase();
                        const isTallFramePreview = ['2x6', '4x6', '3x4'].includes(previewSize);
                        const sampleCount = scopedSampleImagesForDialog.length;
                        return (
                      <Box
                        sx={{
                          width: { xs: 'min(88vw, 280px)', md: 300 },
                          aspectRatio: '1 / 1.45',
                          borderRadius: 1,
                          border: 'none',
                          backgroundColor: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          position: 'relative',
                          boxShadow: '0 18px 34px rgba(0, 0, 0, 0.22)',
                          p: { xs: 1, md: 1.2 },
                        }}
                      >
                        {previewBackgroundUrl ? (
                          <>
                            <Box
                              component="img"
                              src={previewBackgroundUrl}
                              alt="background-preview"
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'center',
                                display: 'block',
                                zIndex: 0,
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                background: 'rgba(255,255,255,0.35)',
                                pointerEvents: 'none',
                                zIndex: 0,
                              }}
                            />
                          </>
                        ) : null}
                        {previewMainImageUrl ? (
                          <Box
                            component="img"
                            src={previewMainImageUrl}
                            alt="combined-frame-preview"
                            sx={{
                              position: 'absolute',
                              left: '50%',
                              top: '40%',
                              transform: 'translate(-50%, -50%)',
                              width: isTallFramePreview ? '78%' : '92%',
                              maxHeight: isTallFramePreview ? '68%' : '72%',
                              objectFit: 'contain',
                              border: 'none',
                              borderRadius: 0,
                              backgroundColor: 'transparent',
                              // Tạo viền bám theo chính shape của ảnh khung (không bọc ô chứa).
                              filter:
                                'drop-shadow(1px 0 0 #cbd5e1) drop-shadow(-1px 0 0 #cbd5e1) ' +
                                'drop-shadow(0 1px 0 #cbd5e1) drop-shadow(0 -1px 0 #cbd5e1)',
                              zIndex: 1,
                            }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ zIndex: 1 }}>
                            Chưa có khung chính để preview.
                          </Typography>
                        )}
                        <Box
                          sx={{
                            position: 'absolute',
                            left: '50%',
                            bottom: { xs: 26, md: 34 },
                            transform: 'translateX(-50%)',
                            zIndex: 1,
                            px: 2.4,
                            py: 1.15,
                            borderRadius: 999,
                            backgroundColor: '#0f766e',
                            color: '#fff',
                            fontSize: 13,
                            fontWeight: 700,
                            lineHeight: 1,
                            boxShadow: '0 6px 16px rgba(15,118,110,0.25)',
                          }}
                        >
                          {`Xem khung mẫu${sampleCount > 0 ? ` (${sampleCount})` : ''}`}
                        </Box>
                      </Box>
                        );
                      })()}
                    </Box>
                  </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ mt: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Chọn background đã upload (dùng chung nhiều album)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => scrollBackgroundCarousel('left')}
                            disabled={loadingSharedFrameBackgrounds || sharedFrameBackgrounds.length === 0}
                          >
                            Trước
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => scrollBackgroundCarousel('right')}
                            disabled={loadingSharedFrameBackgrounds || sharedFrameBackgrounds.length === 0}
                          >
                            Sau
                          </Button>
                        </Box>
                      </Box>
                      <Box
                        ref={backgroundCarouselRef}
                        sx={{
                          display: 'flex',
                          gap: 1.25,
                          overflowX: 'auto',
                          py: 0.5,
                          pr: 0.5,
                          width: '100%',
                          scrollSnapType: 'x mandatory',
                          '&::-webkit-scrollbar': { height: 8 },
                          '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: 8 },
                        }}
                      >
                        {loadingSharedFrameBackgrounds ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1 }}>
                            <CircularProgress size={18} />
                            <Typography variant="caption" color="text.secondary">
                              Đang tải thư viện background...
                            </Typography>
                          </Box>
                        ) : sharedFrameBackgrounds.length === 0 ? (
                          <Typography variant="caption" color="text.secondary" sx={{ py: 1 }}>
                            Chưa có background dùng chung.
                          </Typography>
                        ) : (
                          sharedFrameBackgrounds.map((item) => {
                            const isSelected = item.url === String(frameDetail?.config?.backgroundImage?.url || '').trim();
                            const isDeleting = deletingSharedBackgroundId === item._id;
                            const isBusy =
frameDetailUploadingBackground || frameDetailUploadingSamples ||
                              Boolean(deletingSharedBackgroundId);
                            return (
                              <Paper
                                key={item._id}
                                variant="outlined"
                                onClick={() => {
                                  if (isBusy) return;
                                  if (isSelected) {
                                    deleteBackgroundImage();
                                    return;
                                  }
                                  applyBackgroundFromLibrary(item._id);
                                }}
                                sx={{
                                  minWidth: 140,
                                  width: 140,
                                  borderRadius: 1.25,
                                  p: 0.75,
                                  scrollSnapAlign: 'start',
                                  borderColor: isSelected ? '#14b8a6' : '#e2e8f0',
                                  backgroundColor: isSelected ? '#f0fdfa' : '#ffffff',
                                  cursor: isBusy ? 'not-allowed' : isSelected ? 'default' : 'pointer',
                                  transition: 'all 0.15s ease',
                                  '&:hover': isBusy || isSelected ? {} : { borderColor: '#f472b6', transform: 'translateY(-1px)' },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: '100%',
                                    aspectRatio: '1 / 1.45',
                                    borderRadius: 1,
                                    border: '1px solid #e2e8f0',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#ffffff',
                                  }}
                                >
                                  {sharedBackgroundPreviewErrorMap[item._id] ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ px: 1, textAlign: 'center' }}>
                                      Ảnh lỗi hoặc URL không hợp lệ
                                    </Typography>
                                  ) : (
                                    <Box
                                      component="img"
                                      src={item.url}
                                      alt={item.name || 'shared-background'}
                                      sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        display: 'block',
                                      }}
                                      onError={() =>
                                        setSharedBackgroundPreviewErrorMap((prev) => ({
                                          ...prev,
                                          [item._id]: true,
                                        }))
                                      }
                                      onLoad={() =>
                                        setSharedBackgroundPreviewErrorMap((prev) => {
                                          if (!prev[item._id]) return prev;
                                          const next = { ...prev };
                                          delete next[item._id];
                                          return next;
                                        })
                                      }
                                    />
                                  )}
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    mt: 0.9,
                                    width: '100%',
                                    display: 'block',
                                    textAlign: 'center',
                                    fontWeight: 700,
                                    color: isSelected ? '#0f766e' : '#f43f5e',
                                  }}
                                >
                                  {isSelected ? 'Đang dùng' : 'Click để áp dụng'}
                                </Typography>
                                <Button
                                  size="small"
                                  color="error"
                                  variant="text"
                                  sx={{ mt: 0.25, width: '100%' }}
                                  disabled={isBusy}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    deleteSharedBackground(item);
                                  }}
                                >
                                  {isDeleting ? 'Đang xóa...' : 'Xóa'}
                                </Button>
                              </Paper>
                            );
                          })
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  mt: 2.5,
                  p: 1.5,
                  borderRadius: 1.5,
                  borderColor: '#bfdbfe',
                  backgroundColor: '#f8fbff',
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Danh sách khung mẫu (riêng từng album / từng frame): upload tối đa 10 ảnh mỗi lần.
                </Typography>
                <Box
                  component="label"
                  sx={{
                    ...uploadDropSx,
                    cursor:
frameDetailUploadingBackground || frameDetailUploadingSamples
                        ? 'not-allowed'
                        : 'pointer',
                    opacity:
frameDetailUploadingBackground || frameDetailUploadingSamples
                        ? 0.6
                        : 1,
                  }}
                >
                  {frameDetailUploadingSamples ? (
                    <CircularProgress size={26} />
                  ) : (
                    <CloudUploadRounded sx={{ color: '#eba3bf', fontSize: 32 }} />
                  )}
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#1f2937' }}>
                    {frameDetailUploadingSamples ? 'Đang upload...' : 'Drag and drop photos here, or click to select'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Maximum 10 photos at once
                  </Typography>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    disabled={
                      frameDetailUploadingBackground || frameDetailUploadingSamples
                    }
                    onChange={(e) => {
                      uploadSampleImages(e.target.files);
                      e.currentTarget.value = '';
                    }}
                  />
                </Box>
              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  {scopedSampleImagesForDialog.map((sample) => (
                    <Grid item xs={12} sm={6} md={4} key={sample._id || sample.url}>
                      <Paper variant="outlined" sx={{ p: 1 }}>
                        <Box
                          component="img"
                          src={sample.url}
                          alt={sample.name || 'sample-frame'}
                          sx={{
                            width: '100%',
                            height: 120,
                            objectFit: 'contain',
                            backgroundColor: '#fafafa',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={!sample._id || deletingSampleId === sample._id}
                            onClick={() => sample._id && deleteSampleImage(sample._id)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                  {scopedSampleImagesForDialog.length === 0 && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Chưa có ảnh mẫu.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFrameDetailDialog}>Đóng</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={coverCropOpen}
        onClose={closeCoverCropper}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            width: '92vw',
            maxWidth: '980px',
          },
        }}
      >
        <DialogTitle>Cắt ảnh bìa theo khung trang</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Kéo ảnh để di chuyển và zoom trước khi dùng làm ảnh bìa.
          </Typography>
          <Box
            sx={{
              width: '100%',
              border: '1px solid #e5e7eb',
              borderRadius: 1.5,
              p: 1,
              backgroundColor: '#f8fafc',
              display: 'flex',
              justifyContent: 'center',
            }}
            onPointerDown={handleCoverCropPointerDown}
            onPointerMove={handleCoverCropPointerMove}
            onPointerUp={handleCoverCropPointerUp}
            onPointerCancel={handleCoverCropPointerUp}
            onTouchStart={handleCoverCropTouchStart}
            onTouchMove={handleCoverCropTouchMove}
            onTouchEnd={handleCoverCropTouchEnd}
            onTouchCancel={handleCoverCropTouchEnd}
          >
            <canvas
              ref={coverCropCanvasRef}
              style={{
                width: 'min(100%, 340px)',
                aspectRatio: `${FRAME_BG_CROP_WIDTH} / ${FRAME_BG_CROP_HEIGHT}`,
                display: 'block',
                borderRadius: '8px',
                border: '1px solid #dbe4f0',
                backgroundColor: '#fff',
                touchAction: 'none',
                cursor: coverCropDragging ? 'grabbing' : 'grab',
              }}
            />
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Typography variant="caption" color="text.secondary">
              Zoom
            </Typography>
            <Slider
              value={coverCropZoom}
              min={FRAME_BG_ZOOM_MIN}
              max={FRAME_BG_ZOOM_MAX}
              step={0.05}
              onChange={(_, value) => setCoverCropZoom(Number(value))}
              disabled={coverCropApplying}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCoverCropper} disabled={coverCropApplying}>
            Hủy
          </Button>
          <Button variant="contained" onClick={applyCoverCrop} disabled={coverCropApplying || !coverCropSrc}>
            {coverCropApplying ? 'Đang xử lý...' : 'Áp dụng ảnh bìa'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={frameBgCropOpen}
        onClose={closeFrameBackgroundCropper}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            width: '92vw',
            maxWidth: '980px',
          },
        }}
      >
        <DialogTitle>Cắt ảnh background theo khung trang</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Chỉnh vị trí và zoom ảnh trong khung trang trước khi upload.
          </Typography>
          <Box
            sx={{
              width: '100%',
              border: '1px solid #e5e7eb',
              borderRadius: 1.5,
              p: 1,
              backgroundColor: '#f8fafc',
              display: 'flex',
              justifyContent: 'center',
            }}
            onPointerDown={handleFrameBgCropPointerDown}
            onPointerMove={handleFrameBgCropPointerMove}
            onPointerUp={handleFrameBgCropPointerUp}
            onPointerCancel={handleFrameBgCropPointerUp}
            onTouchStart={handleFrameBgCropTouchStart}
            onTouchMove={handleFrameBgCropTouchMove}
            onTouchEnd={handleFrameBgCropTouchEnd}
            onTouchCancel={handleFrameBgCropTouchEnd}
          >
            <canvas
              ref={frameBgCropCanvasRef}
              style={{
                width: 'min(100%, 340px)',
                aspectRatio: `${FRAME_BG_CROP_WIDTH} / ${FRAME_BG_CROP_HEIGHT}`,
                display: 'block',
                borderRadius: '8px',
                border: '1px solid #dbe4f0',
                backgroundColor: '#fff',
                touchAction: 'none',
                cursor: frameBgCropDragging ? 'grabbing' : 'grab',
              }}
            />
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Typography variant="caption" color="text.secondary">
              Zoom
            </Typography>
            <Slider
              value={frameBgCropZoom}
              min={FRAME_BG_ZOOM_MIN}
              max={FRAME_BG_ZOOM_MAX}
              step={0.05}
              onChange={(_, value) => setFrameBgCropZoom(Number(value))}
              disabled={frameBgCropApplying}
            />
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={closeFrameBackgroundCropper} disabled={frameBgCropApplying}>
            Hủy
          </Button>
          <Button variant="contained" onClick={applyFrameBackgroundCrop} disabled={frameBgCropApplying || !frameBgCropSrc}>
            {frameBgCropApplying ? 'Đang xử lý...' : 'Cắt & upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(albumToDelete)}
        onClose={() => {
          if (confirmDeleting) return;
          setAlbumToDelete(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Xóa trang album?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Bạn có chắc muốn xóa trang album {`"${albumToDelete?.name || ''}"`} không?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAlbumToDelete(null)}
            disabled={confirmDeleting}
          >
            {t.common.cancel}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmRemoveAlbum}
            disabled={confirmDeleting}
          >
            {confirmDeleting ? t.common.updating : t.common.delete}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(sharedBackgroundToDelete)}
        onClose={() => {
          if (confirmDeleting) return;
          setSharedBackgroundToDelete(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Xóa background khỏi thư viện?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Background này sẽ bị gỡ khỏi các frame đang dùng. Bạn có chắc muốn xóa không?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSharedBackgroundToDelete(null)}
            disabled={confirmDeleting}
          >
            Hủy
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteSharedBackground}
            disabled={confirmDeleting}
          >
            {confirmDeleting ? 'Đang xóa...' : 'Xóa'}
          </Button>
        </DialogActions>
      </Dialog>
    </UserPageShell>
  );
};

export default PublicAlbumPages;
